// OTTracker — main app shell. Domain data lives in constants.js, pure helpers
// in calc.js, PocketBase access + sync bus in pb.js, the localStorage cache in
// storage.js, and shared UI in icons.js / components.js / AuthScreens.js /
// AdminUsers.js. This file is only the signed-in App component.
import { useState, useMemo, useEffect } from "react";
import "./ottracker.css";
import {
  ATTENDANCE_CODES, OTHER_CODES, OT_CODES, COMP_WAGE_TYPES, SHORT_CODE, TIME_OPTIONS,
  BLANK, BLANK_EXP, EXP_TYPES, PER_DIEM_KEYS, OTHER_EXP_KEYS, PER_DIEM_RATES, FULL_DAY_RATE, TIME_ACCOUNTS,
} from "./constants";
import {
  isAbs, entryMins, entryHrs, calcMins, minsToHrs, fmtHrs, fmtDur, fmtTime, decToTimeStr,
  localISODate, addDays, getPeriodDates, getPeriodEnd, stepPeriod, periodStartForDate, getCurrentPeriodStart,
  dateLbl, periodRange, timeAgo, money, monthKey, monthLabel, monthLabelLong, stepMonth, monthFirstISO, monthLastISO,
  isAdmin, parseResetToken, buildPerDiemEntries, perDiemSelectionValid, buildESSExpenseRows, buildEssComments, essDate,
  receiptFilename, downloadDataUrl, resizeImage, csvCell, mergeByDate, mergeNotes, entrySig, expenseSig,
  suggestDoubleOt, accountBalance, bankedBreakdown, mergeTimeAccounts,
} from "./calc";
import {
  pb, EXPORT_URL, onSync, syncPending,
  pbSaveEntry, pbDeleteEntry, pbLoadEntries, pbSaveEmployee, pbLoadEmployee,
  pbLoadDayNotes, pbSaveDayNote, pbDeleteDayNote, pbLoadExpenses, pbSaveExpense, pbDeleteExpense,
  pbLoadTimeAccounts, pbSaveTimeAccount,
} from "./pb";
import {
  purgeLegacyKeys, loadCurrent, saveCurrent, loadTheme, saveTheme, loadLastEntry, saveLastEntry,
  loadLastExp, saveLastExp, loadEmployee, saveEmployee, loadNotes, saveNotes, loadExpenses, saveExpenses,
  loadEssComments, saveEssComments,
  loadTimeAccounts, saveTimeAccounts, loadPendingDeletes, savePendingDeletes,
} from "./storage";
import { Logo, GearIcon, MIcon } from "./icons";
import { SyncChip, ExpCard, FeedEntryCard, CameraCapture } from "./components";
import { LoginScreen, ResetScreen } from "./AuthScreens";
import { AdminUsers } from "./AdminUsers";
import { onSwUpdate, applyUpdate } from "./swUpdate";

export default function App() {
  const [user, setUser]               = useState(pb.authStore.isValid ? pb.authStore.model : null);
  const [pwResetToken]                = useState(()=>parseResetToken(window.location.search));
  const [periodStart, setPeriodStart] = useState(getCurrentPeriodStart);
  const [entries, setEntries]         = useState({});
  const [form, setForm]               = useState({...BLANK});
  const [selDate, setSelDate]         = useState(null);
  const [editId, setEditId]           = useState(null);
  const [editFromDate, setEditFromDate] = useState(null); // original date when editing (date can be changed)
  const [showForm, setShowForm]       = useState(false);
  const [exportMsg, setExportMsg]     = useState("");
  const [view, setView]               = useState("current"); // "current" | "history" | "settings" | "timeoff"
  const [confirmDelete, setConfirmDelete] = useState(null);  // {date, id}
  const [confirmDeleteNote, setConfirmDeleteNote] = useState(null); // date string
  const [employee, setEmployee]         = useState({name:"",employeeId:"",department:""});
  const [showPrint, setShowPrint]       = useState(null); // period start to print
  const [showESS, setShowESS]           = useState(null); // period start for the ESS helper
  const [showESSExp, setShowESSExp]     = useState(null); // {month: YYYY-MM, cc: "62"|"60"} for the ESS Expenses helper
  const [essCopied, setEssCopied]       = useState(null); // key of last-copied ESS field
  const [essComments, setEssComments]   = useState({}); // {"YYYY-MM|cc": manually-typed comments text}
  const [importData, setImportData]     = useState(null); // staged JSON import {raw, entries, expenses, notes}
  const [empForm, setEmpForm]           = useState({name:"",employeeId:"",department:""});
  const [theme, setTheme]               = useState("system");
  const [lastEntry, setLastEntry]       = useState(null);
  const [lastExp, setLastExp]           = useState(null);
  const [feedFilter, setFeedFilter]     = useState("all"); // all | ot | absence | expenses | notes
  const [dayNotes, setDayNotes]         = useState({});   // {date: {pbId, note}}
  const [noteEdit, setNoteEdit]         = useState(null); // {date, text} editor
  const [expenses, setExpenses]         = useState({});   // {date: [expense,...]}
  const [timeAccounts, setTimeAccounts] = useState({}); // {account: {pbId?, hours, asOf}} — manual anchors
  const [acctEdit, setAcctEdit]         = useState(null); // {account, hours, asOf} inline editor draft
  const [showExpForm, setShowExpForm]   = useState(false);
  const [showCamera, setShowCamera]     = useState(false); // camera-capture overlay for the receipt zone
  const [expForm, setExpForm]           = useState({...BLANK_EXP});
  const [expEditId, setExpEditId]       = useState(null);
  const [expSelDate, setExpSelDate]     = useState(null);
  const [confirmDeleteExp, setConfirmDeleteExp] = useState(null); // {date,id}
  const [receiptView, setReceiptView]   = useState(null); // data URL to view full-screen
  const [online, setOnline]             = useState(typeof navigator==="undefined" ? true : navigator.onLine!==false);
  const [syncSnap, setSyncSnap]         = useState({pending:0,last:null,storageWarn:false});
  const [dismissedSyncError, setDismissedSyncError] = useState(null); // diagnostic banner, see below
  const [updateReady, setUpdateReady]   = useState(false); // new SW build waiting
  const [, setSyncTick]                 = useState(0); // forces "Xm ago" to refresh

  useEffect(()=>{
    // One-time cleanup of the old account-agnostic cache keys — they may hold a
    // previous user's data (storage.js scopes every data key per user now).
    purgeLegacyKeys();
    const th=loadTheme(); setTheme(th);
  },[]);

  // Patch a freshly-created record's PocketBase id back into a {date: [...]}
  // map, matching by local id (never by position — a second add can land first).
  const patchPbId = (map, date, localId, pbId) => {
    const day = [...(map[date]||[])];
    const i = day.findIndex(r => r.id === localId);
    if (i === -1) return map;
    day[i] = { ...day[i], pbId };
    return { ...map, [date]: day };
  };

  // Clears the dirty flag once an offline-queued edit's push actually succeeds
  // (same id-matching rule as patchPbId — never by array position).
  const clearDirty = (map, date, localId) => {
    const day = [...(map[date]||[])];
    const i = day.findIndex(r => r.id === localId);
    if (i === -1) return map;
    const { dirty, ...rest } = day[i];
    day[i] = rest;
    return { ...map, [date]: day };
  };
  // Stage a delete that must reach PocketBase — retried by syncAll until the
  // server confirms it, so a failed offline delete can't be silently undone.
  function addPendingDelete(collection, pbId) {
    const p = loadPendingDeletes();
    if (!(p[collection]||[]).includes(pbId)) savePendingDeletes({ ...p, [collection]: [...(p[collection]||[]), pbId] });
  }
  function removePendingDelete(collection, pbId) {
    const p = loadPendingDeletes();
    savePendingDeletes({ ...p, [collection]: (p[collection]||[]).filter(x => x !== pbId) });
  }
  // Retries every delete that hasn't been confirmed by the server yet — runs
  // as part of every syncAll (sign-in, sync-chip tap, reconnect).
  function retryPendingDeletes() {
    const pending = loadPendingDeletes();
    (pending.entries||[]).forEach(pbId => { pbDeleteEntry(pbId).then(ok => { if (ok) removePendingDelete("entries", pbId); }); });
    (pending.expenses||[]).forEach(pbId => { pbDeleteExpense(pbId).then(ok => { if (ok) removePendingDelete("expenses", pbId); }); });
    (pending.day_notes||[]).forEach(pbId => { pbDeleteDayNote(pbId).then(ok => { if (ok) removePendingDelete("day_notes", pbId); }); });
  }

  // Full PocketBase pull + heal cycle (server is the base; local no-pbId
  // records are kept AND pushed up; signature dedupe stops duplicates).
  // Runs on sign-in, on tapping the sync chip, and on reconnect.
  // The pending-counter guard stops overlapping runs from double-pushing
  // unsynced records (two concurrent creates would both succeed).
  // Reads local data via storage.js, never React state — the reconnect listener holds a stale-render instance of this function.
  function syncAll(userId) {
    if (syncPending() > 0) return;
    const pending = loadPendingDeletes();
    pbLoadEntries(userId).then(remote => {
      if(!remote) return; // load failed (offline/error) — keep the local cache, don't wipe
      const { merged, unsynced } = mergeByDate(remote, loadCurrent()?.entries || {}, entrySig, pending.entries);
      setEntries(merged);
      unsynced.forEach(({date,rec})=>{
        pbSaveEntry(rec, date, periodStartForDate(date), userId).then(newId=>{
          if(newId) setEntries(p=>clearDirty(rec.pbId ? p : patchPbId(p, date, rec.id, newId), date, rec.id));
        });
      });
    });
    pbLoadExpenses(userId).then(remote => {
      if(!remote) return;
      const { merged, unsynced } = mergeByDate(remote, loadExpenses(), expenseSig, pending.expenses);
      setExpenses(merged);
      unsynced.forEach(({date,rec})=>{
        pbSaveExpense(rec, userId).then(pbId=>{
          if(pbId) setExpenses(p=>clearDirty(rec.pbId ? p : patchPbId(p, date, rec.id, pbId), date, rec.id));
        });
      });
    });
    pbLoadDayNotes(userId).then(remote => {
      if(!remote) return;
      const { merged, unsynced } = mergeNotes(remote, loadNotes(), pending.day_notes);
      setDayNotes(merged);
      unsynced.forEach(({date,note,pbId})=>{
        pbSaveDayNote(date, note, pbId||null, userId).then(newId=>{
          if(newId) setDayNotes(p=>({ ...p, [date]: { ...(p[date]||{}), pbId: newId, dirty: false } }));
        });
      });
    });
    pbLoadTimeAccounts(userId).then(remote => {
      if(!remote) return; // load failed (offline/error) — keep the local cache
      const { merged, unsynced } = mergeTimeAccounts(remote, loadTimeAccounts());
      setTimeAccounts(merged);
      unsynced.forEach(({account,rec})=>{
        pbSaveTimeAccount(account, rec, userId).then(pbId=>{
          if(pbId) setTimeAccounts(p=>({ ...p, [account]: { ...(p[account]||{}), pbId, dirty: false } }));
        });
      });
    });
    pbLoadEmployee(userId).then(emp => { if(emp) { setEmployee(emp); setEmpForm(emp); saveEmployee(emp); }});
    // Pull the latest theme saved to the account (so it follows across devices).
    pb.collection("users").authRefresh().then(res => {
      const t = res && res.record && res.record.theme;
      if (["system","light","dark"].includes(t)) setTheme(t);
    }).catch(()=>{});
    // Retry any deletes that didn't reach the server yet.
    retryPendingDeletes();
  }

  useEffect(()=>{
    // Runs on sign-in (and on mount when a stored session exists).
    if(!user) return;
    const userId = user.id;
    // 1) Offline-first: hydrate instantly from this user's local cache.
    const s=loadCurrent(); if(s){ setPeriodStart(s.periodStart||getCurrentPeriodStart()); setEntries(s.entries||{}); }
    const emp=loadEmployee(); setEmployee(emp); setEmpForm(emp);
    setLastEntry(loadLastEntry());
    setLastExp(loadLastExp());
    setDayNotes(loadNotes());
    setExpenses(loadExpenses());
    setEssComments(loadEssComments());
    setTimeAccounts(loadTimeAccounts());
    // 2) Pull from PocketBase + heal unsynced local records.
    syncAll(userId);
  },[user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{ saveCurrent({periodStart,entries}); },[periodStart,entries]);
  useEffect(()=>{ saveNotes(dayNotes); },[dayNotes]);
  useEffect(()=>{ saveExpenses(expenses); },[expenses]);
  useEffect(()=>{ saveEssComments(essComments); },[essComments]);
  useEffect(()=>{ saveTimeAccounts(timeAccounts); },[timeAccounts]);
  useEffect(()=>{
    const off = onSync(s=>setSyncSnap(s));
    const goOn=()=>setOnline(true), goOff=()=>setOnline(false);
    window.addEventListener("online",goOn);
    window.addEventListener("offline",goOff);
    const tick = setInterval(()=>setSyncTick(t=>t+1), 30000);
    return ()=>{ off(); window.removeEventListener("online",goOn); window.removeEventListener("offline",goOff); clearInterval(tick); };
  },[]);
  useEffect(()=>onSwUpdate(()=>setUpdateReady(true)),[]);
  // Coming back into service auto-heals: push offline-created records up the
  // moment the browser reports connectivity, no tap or reopen needed.
  // (syncAll identity changes per render; safe to omit from deps — it reads localStorage, not state)
  useEffect(()=>{
    if(!user) return;
    const h=()=>syncAll(user.id);
    window.addEventListener("online",h);
    return ()=>window.removeEventListener("online",h);
  },[user]); // eslint-disable-line react-hooks/exhaustive-deps
  const chipState = !online ? "offline" : (syncSnap.pending>0 ? "syncing" : (syncSnap.storageWarn ? "storage" : "synced"));
  const chipSub   = chipState==="offline" ? "will sync" : chipState==="syncing" ? (syncSnap.pending+" pending") : chipState==="storage" ? "receipts not cached" : timeAgo(syncSnap.last);
  useEffect(()=>{
    const root=document.documentElement;
    if(theme==="dark") root.setAttribute("data-theme","dark");
    else if(theme==="light") root.setAttribute("data-theme","light");
    else root.removeAttribute("data-theme");
    // Keep the <html> background as an inline style so tab repaints never flash the
    // wrong theme (matches the pre-paint script in index.html).
    const dark = theme==="dark" || (theme!=="light" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
    root.style.backgroundColor = dark ? "oklch(0.205 0.012 262)" : "oklch(0.971 0.005 255)";
    saveTheme(theme);
  },[theme]);

  // User picked a theme: apply locally + save to their account so it follows them
  // to other devices.
  function changeTheme(t) {
    setTheme(t);
    if (pb.authStore.isValid) {
      pb.collection("users").update(pb.authStore.model.id, { theme: t }).catch(()=>{});
    }
  }

  const [isDesktop, setIsDesktop] = useState(()=>window.innerWidth>=900);
  useEffect(()=>{
    const h=()=>setIsDesktop(window.innerWidth>=900);
    window.addEventListener("resize",h);
    return ()=>window.removeEventListener("resize",h);
  },[]);

  const dates = useMemo(()=>getPeriodDates(periodStart),[periodStart]);
  const week1 = dates.slice(0,7), week2 = dates.slice(7,14);
  // LOCAL calendar date — toISOString() would be the UTC date, which is
  // tomorrow from ~9pm Atlantic onward (see localISODate in calc.js).
  const [today, setToday] = useState(()=>localISODate());
  useEffect(()=>{
    const tick=()=>{
      const now=new Date();
      setToday(localISODate(now));
      // schedule next tick at the next midnight
      const msToMidnight=new Date(now.getFullYear(),now.getMonth(),now.getDate()+1)-now;
      return setTimeout(tick,msToMidnight);
    };
    const now=new Date();
    const msToMidnight=new Date(now.getFullYear(),now.getMonth(),now.getDate()+1)-now;
    const t=setTimeout(tick,msToMidnight);
    return()=>clearTimeout(t);
  },[]);

  // Absences (Sick 0200 / Medical 0203) are records, not worked hours — they
  // don't count toward any hour total.
  const dayHrs = (d,ents=entries) => (ents[d]||[]).reduce((s,e)=>s+(isAbs(e.attendanceCode)?0:entryHrs(e)),0);
  const wkHrs  = (w,ents=entries) => w.reduce((s,d)=>s+dayHrs(d,ents),0);
  const w1H=wkHrs(week1), w2H=wkHrs(week2), totH=w1H+w2H;
  const paidH   = useMemo(()=>dates.reduce((s,d)=>s+(entries[d]||[]).filter(e=>!isAbs(e.attendanceCode)&&e.compWageType==="0").reduce((ss,e)=>ss+entryHrs(e),0),0),[entries,dates]);
  const bankedH = useMemo(()=>dates.reduce((s,d)=>s+(entries[d]||[]).filter(e=>!isAbs(e.attendanceCode)&&e.compWageType==="1").reduce((ss,e)=>ss+entryHrs(e),0),0),[entries,dates]);
  // Worked overtime (0801) vs standby/on-call (0804) — shown separately so a big
  // standby total doesn't read as worked OT.
  const otH      = useMemo(()=>dates.reduce((s,d)=>s+(entries[d]||[]).filter(e=>e.attendanceCode==="0801").reduce((ss,e)=>ss+entryHrs(e),0),0),[entries,dates]);
  const standbyH = useMemo(()=>dates.reduce((s,d)=>s+(entries[d]||[]).filter(e=>e.attendanceCode==="0804").reduce((ss,e)=>ss+entryHrs(e),0),0),[entries,dates]);

  // Pay periods that actually have entries, newest first — derived live from
  // entries (no cached snapshots). Drives the History list.
  const historyPeriods = useMemo(()=>{
    const set = new Set();
    Object.entries(entries).forEach(([date,arr])=>{ if(arr&&arr.length) set.add(periodStartForDate(date)); });
    return [...set].sort().reverse();
  },[entries]);

  // Switching periods just changes which 14 days are shown. All entries are
  // already in memory (loaded from PocketBase, the single source of truth), so
  // there's no snapshotting/caching to go stale.
  function switchPeriod(v) {
    if(v===periodStart) return;
    setPeriodStart(v);
  }

  function openAdd(d)    { setSelDate(d); setEditFromDate(null); setForm({...BLANK}); setEditId(null); setShowForm(true); setShowExpForm(false); }
  function openEdit(d,id,ents=null) {
    const src = ents || entries;
    const e=(src[d]||[]).find(x=>x.id===id); if(!e)return;
    setSelDate(d); setEditFromDate(d);
    setForm({attendanceCode:e.attendanceCode||"0801",compWageType:e.compWageType||"0",otCode:e.otCode||"62",doubleOt:!!e.doubleOt,orderProject:e.orderProject||"",reason:e.reason||"",beforeStart:e.beforeStart||"",beforeEnd:e.beforeEnd||"",afterStart:e.afterStart||"",afterEnd:e.afterEnd||"",duringStart:e.duringStart||"",duringEnd:e.duringEnd||""});
    setEditId(id); setShowForm(true);
  }

  function saveEntry() {
    if(entryMins(form)<=0) return;
    // The date can be changed in the form; fromDate is where the entry currently lives.
    const fromDate = editId ? (editFromDate||selDate) : selDate;
    const moved = !!editId && fromDate!==selDate;
    // Preserve the original entry's pbId so an edit UPDATES (and survives a date move)
    // instead of creating a duplicate. dirty:true marks it for retry if the push
    // below fails (offline) — cleared once the push actually succeeds.
    let origPbId;
    if(editId){
      origPbId = ((entries[fromDate]||[]).find(e=>e.id===editId)||{}).pbId;
    }
    const entry = {...form, id: editId||Date.now().toString(), ...(origPbId?{pbId:origPbId, dirty:true}:{})};
    // Single source of truth: the live entries map (keyed by date). Works for any
    // period — current or a past one you've navigated into.
    setEntries(p=>{
      const next={...p};
      if(moved) next[fromDate]=(next[fromDate]||[]).filter(e=>e.id!==editId);
      const d=[...(next[selDate]||[])];
      if(editId){const i=d.findIndex(e=>e.id===editId);if(i!==-1)d[i]=entry;else d.push(entry);}
      else d.push(entry);
      next[selDate]=d;
      return next;
    });
    const {id:_id,pbId:_pb,...formWithoutId}=form; saveLastEntry(formWithoutId);
    setLastEntry(formWithoutId);
    // Sync to PocketBase — store the period derived from the entry's own date.
    if(pb.authStore.isValid) {
      const userId = pb.authStore.model.id;
      pbSaveEntry(entry, selDate, periodStartForDate(selDate), userId).then(newPbId => {
        // Match by the entry's own id — a second add can land before this
        // resolves, so "last in the array" is not necessarily this entry.
        if(newPbId) setEntries(prev => clearDirty(editId ? prev : patchPbId(prev, selDate, entry.id, newPbId), selDate, entry.id));
      });
    }
    setShowForm(false);
  }

  function saveTimeAccount() {
    if(!acctEdit) return;
    const hours = parseFloat(acctEdit.hours);
    if(!(hours >= 0) || !acctEdit.asOf) return;
    const account = acctEdit.account;
    const existing = timeAccounts[account]||{};
    // dirty:true marks an edit to an already-synced anchor for retry if the
    // push below fails (offline) — cleared once the push actually succeeds.
    const rec = { ...existing, hours, asOf: acctEdit.asOf, ...(existing.pbId ? { dirty:true } : {}) };
    setTimeAccounts(p=>({ ...p, [account]: rec }));
    if(pb.authStore.isValid) {
      pbSaveTimeAccount(account, rec, pb.authStore.model.id).then(pbId=>{
        if(pbId) setTimeAccounts(p=>({ ...p, [account]: { ...(p[account]||{}), pbId, dirty:false } }));
      });
    }
    setAcctEdit(null);
  }

  function logout() {
    // Clear the session AND all in-memory data so nothing from this account is
    // still on screen (or gets written to the next account's cache) after
    // sign-out. The per-user localStorage cache stays for the next sign-in.
    pb.authStore.clear();
    setEntries({}); setExpenses({}); setDayNotes({}); setTimeAccounts({}); setAcctEdit(null);
    setEmployee({name:"",employeeId:"",department:""}); setEmpForm({name:"",employeeId:"",department:""});
    setLastEntry(null); setLastExp(null);
    setPeriodStart(getCurrentPeriodStart());
    setView("current");
    setUser(null);
  }

  function saveDayNote() {
    if(!noteEdit) return;
    const { date, text } = noteEdit;
    const trimmed = (text||"").trim();
    setDayNotes(prev => {
      const cur = prev[date];
      let next;
      if (!trimmed) {
        next = { ...prev }; delete next[date];
        if (cur && cur.pbId) {
          addPendingDelete("day_notes", cur.pbId);
          if (pb.authStore.isValid) pbDeleteDayNote(cur.pbId).then(ok => { if (ok) removePendingDelete("day_notes", cur.pbId); });
        }
      } else {
        // dirty:true marks an edit to an already-synced note for retry if the
        // push below fails (offline) — cleared once the push actually succeeds.
        next = { ...prev, [date]: { ...cur, note: trimmed, ...(cur && cur.pbId ? { dirty:true } : {}) } };
        if (pb.authStore.isValid) {
          pbSaveDayNote(date, trimmed, cur && cur.pbId, pb.authStore.model.id).then(id => {
            if (id) setDayNotes(p => ({ ...p, [date]: { ...(p[date]||{}), pbId: id, dirty:false } }));
          });
        }
      }
      saveNotes(next);
      return next;
    });
    setNoteEdit(null);
  }
  function confirmDelNote() {
    const date = confirmDeleteNote;
    const cur = dayNotes[date];
    if (cur && cur.pbId) {
      addPendingDelete("day_notes", cur.pbId);
      if (pb.authStore.isValid) pbDeleteDayNote(cur.pbId).then(ok => { if (ok) removePendingDelete("day_notes", cur.pbId); });
    }
    setDayNotes(prev => { const c={...prev}; delete c[date]; saveNotes(c); return c; });
    setConfirmDeleteNote(null);
    setNoteEdit(null);
  }

  function requestDelete(date,id) { setConfirmDelete({date,id}); }
  function confirmDelEntry() {
    const {date,id}=confirmDelete;
    const delEntry = (entries[date]||[]).find(e=>e.id===id);
    if(delEntry?.pbId) {
      addPendingDelete("entries", delEntry.pbId);
      if(pb.authStore.isValid) pbDeleteEntry(delEntry.pbId).then(ok=>{ if(ok) removePendingDelete("entries", delEntry.pbId); });
    }
    setEntries(p=>({...p,[date]:(p[date]||[]).filter(e=>e.id!==id)}));
    setConfirmDelete(null);
  }

  // ── Expenses ──
  function openAddExpense(d) { setExpSelDate(d); setExpForm({...BLANK_EXP, date:d}); setExpEditId(null); setShowExpForm(true); setShowForm(false); setShowCamera(false); }
  function openEditExpense(d,id) {
    const x=(expenses[d]||[]).find(e=>e.id===id); if(!x) return;
    setExpSelDate(d);
    setExpForm({type:x.type||"other",amount:String(x.amount||""),project:x.project||"",reason:x.reason||"",location:x.location||"",receipt:x.receipt||"",date:x.date||d,meals:{},fulldayAmt:"",costCentre:x.costCentre||"62"});
    setExpEditId(id); setShowExpForm(true); setShowCamera(false);
  }
  async function onReceiptPick(ev) {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value="";
    if(!file) return;
    try { const dataUrl = await resizeImage(file); setExpForm(f=>({...f,receipt:dataUrl})); }
    catch(e) { console.warn("receipt resize failed", e); }
  }
  async function onCameraCapture(blob) {
    setShowCamera(false);
    try { const dataUrl = await resizeImage(blob); setExpForm(f=>({...f,receipt:dataUrl})); }
    catch(e) { console.warn("receipt resize failed", e); }
  }
  function saveExpense() {
    // Per-diem mode (new expense only): meals selected → one entry per meal,
    // or one Full Day Meal Per Diem entry when all three are selected.
    const perDiem = !expEditId && expForm.meals && Object.keys(expForm.meals).length > 0;
    if(perDiem) {
      const allThree = PER_DIEM_KEYS.every(k => expForm.meals[k] != null);
      if(allThree) {
        if(!(parseFloat(expForm.fulldayAmt) > 0)) return;
      } else {
        if(!perDiemSelectionValid(expForm.meals)) return;
      }
      const newDate = expForm.date || expSelDate;
      const shared = { project:expForm.project, reason:expForm.reason, location:expForm.location, receipt:expForm.receipt, date:newDate };
      const recs = buildPerDiemEntries(expForm.meals, shared, Date.now(), expForm.fulldayAmt)
        .map(r=>({...r, costCentre: expForm.costCentre||"62"}));
      const first = recs[0];
      const lastExpData = { type:first.type, amount:String(first.amount), project:first.project, reason:first.reason, location:first.location, costCentre:expForm.costCentre||"62" };
      saveLastExp(lastExpData); setLastExp(lastExpData);
      setExpenses(prev=>({...prev, [newDate]:[...(prev[newDate]||[]), ...recs]}));
      if(pb.authStore.isValid) {
        recs.forEach(rec=>{
          pbSaveExpense({...rec}, pb.authStore.model.id).then(pbId=>{
            if(pbId) setExpenses(prev=>{
              const day=[...(prev[newDate]||[])];
              const i=day.findIndex(x=>x.id===rec.id);
              if(i!==-1) day[i]={...day[i],pbId};
              return {...prev,[newDate]:day};
            });
          });
        });
      }
      setShowExpForm(false); setShowCamera(false);
      return;
    }
    const amt = parseFloat(expForm.amount);
    if(!amt || amt<=0) return;
    const newDate = expForm.date || expSelDate;
    const oldDate = expSelDate;
    const id = expEditId || Date.now().toString();
    let existingPbId = null;
    if(expEditId) { const e=(expenses[oldDate]||[]).find(x=>x.id===expEditId); if(e) existingPbId=e.pbId; }
    const rec = { id, type:expForm.type, amount:amt, project:(expForm.project||"").trim(), reason:(expForm.reason||"").trim(), location:(expForm.location||"").trim(), receipt:expForm.receipt||"", date:newDate, costCentre:expForm.costCentre||"62" };
    // dirty:true marks an edit to an already-synced expense for retry if the
    // push below fails (offline) — cleared once the push actually succeeds.
    if(existingPbId) { rec.pbId = existingPbId; rec.dirty = true; }
    // Remember the reusable fields for "Fill from last claim" (not the receipt/date).
    const lastExpData = { type:expForm.type, amount:expForm.amount, project:rec.project, reason:rec.reason, location:rec.location, costCentre:rec.costCentre||"62" };
    saveLastExp(lastExpData); setLastExp(lastExpData);
    setExpenses(prev=>{
      const next={...prev};
      if(expEditId) next[oldDate]=(next[oldDate]||[]).filter(x=>x.id!==expEditId);
      next[newDate]=[...(next[newDate]||[]), rec];
      return next;
    });
    if(pb.authStore.isValid) {
      pbSaveExpense({...rec}, pb.authStore.model.id).then(pbId=>{
        if(pbId) {
          setExpenses(prev=>{
            const day=[...(prev[newDate]||[])];
            const i=day.findIndex(x=>x.id===id);
            if(i===-1) return prev;
            day[i]=existingPbId ? { ...day[i], dirty:false } : { ...day[i], pbId, dirty:false };
            return {...prev,[newDate]:day};
          });
        }
      });
    }
    setShowExpForm(false); setShowCamera(false);
  }
  function deleteExpenseFromForm() {
    if(!expEditId) return;
    const x=(expenses[expSelDate]||[]).find(e=>e.id===expEditId);
    if(x&&x.pbId) {
      addPendingDelete("expenses", x.pbId);
      if(pb.authStore.isValid) pbDeleteExpense(x.pbId).then(ok=>{ if(ok) removePendingDelete("expenses", x.pbId); });
    }
    setExpenses(prev=>({...prev,[expSelDate]:(prev[expSelDate]||[]).filter(e=>e.id!==expEditId)}));
    setShowExpForm(false); setShowCamera(false);
  }
  function requestDeleteExp(date,id) { setConfirmDeleteExp({date,id}); }
  function confirmDelExp() {
    const {date,id}=confirmDeleteExp;
    const x=(expenses[date]||[]).find(e=>e.id===id);
    if(x&&x.pbId) {
      addPendingDelete("expenses", x.pbId);
      if(pb.authStore.isValid) pbDeleteExpense(x.pbId).then(ok=>{ if(ok) removePendingDelete("expenses", x.pbId); });
    }
    setExpenses(prev=>({...prev,[date]:(prev[date]||[]).filter(e=>e.id!==id)}));
    setConfirmDeleteExp(null);
  }
  // Per-diem mode is active for a new expense once one or more meals are picked.
  const perDiemMode    = !expEditId && !!expForm.meals && Object.keys(expForm.meals).length > 0;
  const allThreeSelected = perDiemMode && PER_DIEM_KEYS.every(k => expForm.meals[k] != null);
  const expSaveValid   = perDiemMode
    ? (allThreeSelected ? parseFloat(expForm.fulldayAmt) > 0 : perDiemSelectionValid(expForm.meals))
    : (!!expForm.type && parseFloat(expForm.amount) > 0);
  const expTotal       = perDiemMode
    ? (allThreeSelected
        ? (parseFloat(expForm.fulldayAmt) || 0)
        : Object.values(expForm.meals).reduce((s,v)=>s+(parseFloat(v)||0),0))
    : (parseFloat(expForm.amount)||0);
  function toggleMeal(m) {
    setExpForm(f=>{
      const meals={...(f.meals||{})};
      if(meals[m]!=null) delete meals[m]; else meals[m]=String(PER_DIEM_RATES[m]);
      const nowAllThree = PER_DIEM_KEYS.every(k => meals[k] != null);
      return {...f, meals, fulldayAmt: nowAllThree ? String(FULL_DAY_RATE) : f.fulldayAmt};
    });
  }
  function setMealAmount(m, v) {
    const clean=v.replace(/[^0-9.]/g,"").replace(/(\..*)\./g,"$1");
    setExpForm(f=>({...f, meals:{...f.meals, [m]:clean}}));
  }

  // Monthly expense roll-up (expenses are tracked per calendar month, not per pay period)
  const allExpenses = Object.values(expenses).flat();
  // Expenses roll up by calendar month. A pay period can straddle two months,
  // so anchor on the period midpoint (day 7) — the month it mostly falls in.
  const expAnchor = addDays(periodStart, 7);
  const expMonth = monthKey(expAnchor);
  const expMonthLbl = monthLabel(expAnchor);
  const expMonthList = allExpenses.filter(x=>monthKey(x.date)===expMonth);
  const expMonthTotal = expMonthList.reduce((s,x)=>s+(Number(x.amount)||0),0);
  const expMonthCount = expMonthList.length;

  // Download a complete backup of this user's data as a JSON file (client-side).
  function exportJSON() {
    try {
      const payload = {
        app: "OTTracker",
        version: 1,
        exportedAt: new Date().toISOString(),
        account: user?.email || user?.username || null,
        employee,
        entries,   // all overtime/absence entries, keyed by date
        expenses,  // all expense claims, keyed by date
        dayNotes,  // per-day notes
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ottracker-backup-${localISODate()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setExportMsg("Backup downloaded.");
      setTimeout(()=>setExportMsg(""), 2500);
    } catch(e) { console.warn("JSON export failed:", e); setExportMsg("Backup failed."); }
  }

  // Read a backup file and stage it for a confirm dialog (additive merge — never deletes).
  function importJSON(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (!data || typeof data !== "object" || data.app !== "OTTracker") {
          setExportMsg("Not an OTTracker backup file."); setTimeout(()=>setExportMsg(""), 3500); return;
        }
        const countArr = (m)=> Object.values(m||{}).reduce((s,a)=>s+(Array.isArray(a)?a.length:0),0);
        setImportData({ raw:data, entries:countArr(data.entries), expenses:countArr(data.expenses), notes:Object.keys(data.dayNotes||{}).length });
      } catch(e) { setExportMsg("Couldn't read that file."); setTimeout(()=>setExportMsg(""), 3500); }
    };
    reader.readAsText(file);
  }
  // Merge the backup into the current data (dedup by id, never delete) and sync new items to PB.
  function doImport(data) {
    const userId = pb.authStore.isValid ? pb.authStore.model.id : null;
    // Entries
    const nextE = {...entries}; const addedE = [];
    Object.entries(data.entries||{}).forEach(([date,arr])=>{
      const have = nextE[date]||[]; const ids = new Set(have.map(e=>e.id));
      const add = (arr||[]).filter(e=>e&&e.id&&!ids.has(e.id)).map(({pbId,...r})=>r);
      if (add.length) { nextE[date] = [...have, ...add]; add.forEach(e=>addedE.push({date,e})); }
    });
    setEntries(nextE);
    // Expenses
    const nextX = {...expenses}; const addedX = [];
    Object.entries(data.expenses||{}).forEach(([date,arr])=>{
      const have = nextX[date]||[]; const ids = new Set(have.map(x=>x.id));
      const add = (arr||[]).filter(x=>x&&x.id&&!ids.has(x.id)).map(({pbId,...r})=>r);
      if (add.length) { nextX[date] = [...have, ...add]; add.forEach(x=>addedX.push({date,x})); }
    });
    setExpenses(nextX);
    // Day notes
    const nextN = {...dayNotes}; const addedN = [];
    Object.entries(data.dayNotes||{}).forEach(([date,n])=>{
      if (!nextN[date] && n && n.note) { nextN[date] = {note:n.note}; addedN.push({date,note:n.note}); }
    });
    setDayNotes(nextN);
    // Employee — only fill if currently empty
    if (data.employee && !employee.name) {
      const emp = {name:data.employee.name||"",employeeId:data.employee.employeeId||"",department:data.employee.department||""};
      if (emp.name||emp.employeeId||emp.department) { setEmployee(emp); setEmpForm(emp); saveEmployee(emp); if(userId) pbSaveEmployee(emp, userId); }
    }
    // Sync new items to PocketBase
    if (userId) {
      addedE.forEach(({date,e})=> pbSaveEntry(e, date, periodStartForDate(date), userId).then(pbId=>{ if(pbId) setEntries(p=>{const d=[...(p[date]||[])];const i=d.findIndex(x=>x.id===e.id);if(i!==-1)d[i]={...d[i],pbId};return{...p,[date]:d};}); }));
      addedX.forEach(({date,x})=> pbSaveExpense(x, userId).then(pbId=>{ if(pbId) setExpenses(p=>{const d=[...(p[date]||[])];const i=d.findIndex(y=>y.id===x.id);if(i!==-1)d[i]={...d[i],pbId};return{...p,[date]:d};}); }));
      addedN.forEach(({date,note})=> pbSaveDayNote(date, note, null, userId).then(pbId=>{ if(pbId) setDayNotes(p=>({...p,[date]:{...(p[date]||{}),pbId}})); }));
    }
    setImportData(null);
    setExportMsg(`Imported ${addedE.length} entries, ${addedX.length} expenses, ${addedN.length} notes.`);
    setTimeout(()=>setExportMsg(""), 4000);
  }

  async function exportExcel(exportEntries=entries, pStart=periodStart) {
    setExportMsg("Generating Excel...");
    try {
      const resp = await fetch(EXPORT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": pb.authStore.token },
        body: JSON.stringify({ employee, periodStart: pStart, entries: exportEntries }),
      });
      if(!resp.ok) throw new Error("Server error");
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `OT_${pStart}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      setExportMsg("✓ Excel downloaded");
    } catch(e) {
      // Fallback to CSV if server not running
      exportCSV(exportEntries, getPeriodDates(pStart), pStart);
      setExportMsg("✓ CSV downloaded (start server.py for Excel export)");
    }
    setTimeout(()=>setExportMsg(""),4000);
  }

  function exportCSV(exportEntries=entries, exportDates=dates, pStart=periodStart) {
    const rows=[];
    exportDates.forEach(date=>{
      (exportEntries[date]||[]).forEach(e=>{
        const m=entryMins(e); if(m<=0)return;
        const abs=isAbs(e.attendanceCode);
        const cc=OT_CODES.find(c=>c.value===e.otCode)?.costCentre||"";
        const sV=abs?e.duringStart:(e.beforeStart||e.afterStart);
        const eV=abs?e.duringEnd:(e.afterEnd||e.beforeEnd);
        const dow=dateLbl(date).dow;
        rows.push([exportDates.indexOf(date)<7?1:2,date,e.attendanceCode,abs?"":e.compWageType||"0",e.orderProject||"",abs?"":cc,decToTimeStr(sV)||"",decToTimeStr(eV)||"",["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dow],entryHrs(e),e.reason||""]);
      });
    });
    if(!rows.length){setExportMsg("No entries to export.");return;}
    const hdr=["Week","Date","Attendance","Comp/Wage","Order/Project","Cost Centre","Time Start","Time End","Day","Hours","Reason"];
    // csvCell: RFC-4180 quoting + formula-injection guard (same policy as the
    // server's Excel export).
    const csv=[hdr,...rows].map(r=>r.map(csvCell).join(",")).join("\n");
    const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download=`OT_${pStart}_${getPeriodEnd(pStart)}.csv`; a.click();
  }

  // Form calcs
  const fAbs=isAbs(form.attendanceCode);
  const fBm=calcMins(form.beforeStart,form.beforeEnd), fAm=calcMins(form.afterStart,form.afterEnd), fDm=calcMins(form.duringStart,form.duringEnd);
  const fTot=fAbs?fDm:fBm+fAm, fHrs=minsToHrs(fTot);

  // A reset/first-time link (?pwreset=<token>) takes priority over the login form.
  if(pwResetToken) return <ResetScreen token={pwResetToken} onDone={()=>{ try{ window.history.replaceState({},"",window.location.pathname); }catch(_){} window.location.reload(); }} />;
  if(!user) return <LoginScreen onLogin={u=>{ setUser(u); if(u && ["system","light","dark"].includes(u.theme)) setTheme(u.theme); }} />;

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",color:"var(--text)",fontFamily:"'Hanken Grotesk',sans-serif"}}>
      {/* App-shell styles live in ottracker.css */}

      {/* ═══════════ FEED · current pay period ═══════════ */}
      {view==="current"&&isDesktop&&(
        /* ══ DESKTOP: sidebar + 7-col day grid (from dirDesktop.jsx handoff) ══ */
        <div className={"dt-layout"+((showForm||showExpForm)?" panel-open":"")}>

          {/* ── Sidebar ── */}
          <aside className="dt-sidebar">
            <div className="dt-brand">
              <div className="dt-brand-mark"><Logo size={26}/></div>
              <div>
                <div className="dt-brand-name">OTTracker</div>
                <div className="dt-brand-sub">Overtime timesheet</div>
              </div>
            </div>

            <nav className="dt-nav">
              <button className="dt-nav-item on"><MIcon n="list" s={16}/>Dashboard</button>
              <button className="dt-nav-item" onClick={()=>setView("history")}><MIcon n="history" s={16}/>History</button>
              <button className="dt-nav-item" onClick={()=>setView("settings")}><GearIcon size={16}/>Settings</button>
            </nav>

            <div className="dt-sidebar-sec">Pay period</div>
            <div className="dt-period-card">
              <div className="dt-pc-label">{periodRange(periodStart)}</div>
              {periodStart===getCurrentPeriodStart()&&<div className="dt-pc-badge">Current</div>}
              <div className="dt-pc-stat-row">
                <div className="dt-pc-stat">
                  <div className="dt-pc-stat-val">{fmtHrs(otH)}<i>h</i></div>
                  <div className="dt-pc-stat-lab">Overtime</div>
                </div>
                {standbyH>0&&<>
                  <div className="dt-pc-stat-sep"/>
                  <div className="dt-pc-stat">
                    <div className="dt-pc-stat-val" style={{color:"var(--standby)"}}>{fmtHrs(standbyH)}<i>h</i></div>
                    <div className="dt-pc-stat-lab">Standby</div>
                  </div>
                </>}
              </div>
              <div className="dt-pc-bar-track">
                <div className="dt-pc-bar-fill" style={{width:totH>0?Math.min(100,(paidH/totH)*100)+"%":"0%"}}/>
              </div>
              <div className="dt-pc-bar-legend">
                <span><span className="dt-dot paid"/>Paid {fmtHrs(paidH)}h</span>
                <span><span className="dt-dot banked"/>Banked {fmtHrs(bankedH)}h</span>
              </div>
            </div>

            <div className="dt-sidebar-sec">Expenses · {expMonthLbl}</div>
            <div className="dt-exp-card">
              <div className="dt-exp-amt">{money(expMonthTotal)}</div>
              <div className="dt-exp-sub">{expMonthCount} claim{expMonthCount===1?"":"s"} · monthly</div>
            </div>

            {/* Mini calendar — 14 days of the period */}
            {(()=>{
              const firstDate = new Date(dates[0]+"T00:00:00");
              const calLabel = firstDate.toLocaleString("default",{month:"long",year:"numeric"});
              const startDow = (firstDate.getDay()); // 0=Sun
              return (
                <div className="dt-mcal">
                  <div className="dt-mcal-hd">
                    <span className="dt-mcal-lbl">{calLabel}</span>
                  </div>
                  <div className="dt-mcal-dow">{["S","M","T","W","T","F","S"].map((d,i)=><span key={i}>{d}</span>)}</div>
                  <div className="dt-mcal-grid">
                    {Array.from({length:startDow},(_,i)=><div key={"e"+i}/>)}
                    {dates.map(d=>{
                      const hasE=(entries[d]||[]).length>0||(expenses[d]||[]).length>0;
                      const isT=d===today;
                      const p=new Date(d+"T00:00:00");
                      return (
                        <button key={d} className={"dt-mcal-day"+(isT?" today":"")+(hasE?" has":"")}>
                          {p.getDate()}
                          {hasE&&<span className="dt-mcal-dot"/>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="dt-sidebar-foot">
              <button type="button" className="dt-sync" title="Sync now"
                      onClick={()=>user&&syncAll(user.id)}>
                <span className="dt-sync-dot" style={{background:chipState==="synced"?"var(--money)":chipState==="syncing"?"var(--accent)":"var(--ink-3)"}}/>
                <span>{chipState==="syncing"?"Syncing…":chipState==="offline"?"Offline":chipState==="storage"?"Cache full · "+chipSub:"Synced · "+chipSub}</span>
              </button>
              <button className="dt-signout" onClick={logout}>Sign out</button>
            </div>
          </aside>

          {/* ── Main ── */}
          <div className="dt-main">
            <div className="dt-topbar">
              <div className="dt-pp-nav">
                <button className="dt-pp-btn" onClick={()=>switchPeriod(stepPeriod(periodStart,-1))}><MIcon n="chevL" s={15}/></button>
                <div className="dt-pp-mid">
                  <span className="dt-pp-label">{periodRange(periodStart)}</span>
                  {periodStart===getCurrentPeriodStart()
                    ? <span className="dt-pp-cur">Current</span>
                    : <button className="dt-pp-cur pp-jump" onClick={()=>switchPeriod(getCurrentPeriodStart())}>Jump to current</button>}
                </div>
                <button className="dt-pp-btn" onClick={()=>switchPeriod(stepPeriod(periodStart,1))}><MIcon n="chevR" s={15}/></button>
              </div>
              <div className="dt-topbar-acts">
                <button className="dt-btn primary sm" onClick={()=>openAdd(today)}><MIcon n="plus" s={14}/>Log overtime</button>
                <button className="dt-btn money sm" onClick={()=>openAddExpense(today)}><MIcon n="plus" s={14}/>Add expense</button>
              </div>
            </div>

            <div className="dt-content">
              {[week1,week2].map((wk,wi)=>{
                const wkH=wk.reduce((s,d)=>s+dayHrs(d),0);
                const wkStart=new Date(wk[0]+"T00:00:00");
                const wkEnd=new Date(wk[6]+"T00:00:00");
                const monFmt=d=>d.toLocaleString("default",{month:"short"});
                return (
                  <div className="dt-week" key={wi}>
                    <div className="dt-week-head">
                      <span className="dt-week-name">Week {wi+1}</span>
                      <span className="dt-week-range">{wkStart.getDate()}–{wkEnd.getDate()} {monFmt(wkStart)}</span>
                      <span className="dt-week-tot">{fmtHrs(wkH)}<i>h</i></span>
                    </div>
                    <div className="dt-grid">
                      {wk.map(d=>{
                        const ents=entries[d]||[], xs=expenses[d]||[], note=dayNotes[d]?.note;
                        const dH=dayHrs(d);
                        const isT=d===today;
                        const empty=!ents.length&&!xs.length&&!note;
                        const dp=new Date(d+"T00:00:00");
                        const dowStr=["SUN","MON","TUE","WED","THU","FRI","SAT"][dp.getDay()];
                        return (
                          <div key={d} className={"dt-day"+(isT?" today":"")+(empty?" empty":"")}>
                            <div className="dt-day-head">
                              <div className="dt-day-dow">{dowStr}</div>
                              <div className="dt-day-num">{dp.getDate()}</div>
                              {dH>0&&<div className="dt-day-hrs">{fmtHrs(dH)}h</div>}
                            </div>
                            <div className="dt-day-body">
                              {note
                                ?<button className="dt-daylog" onClick={()=>setNoteEdit({date:d,text:note})}>
                                    <div className="dt-daylog-lab"><MIcon n="pencil" s={9}/>Day note</div>
                                    <div className="dt-daylog-txt">{note}</div>
                                  </button>
                                :<button className="dt-daylog-add" onClick={()=>setNoteEdit({date:d,text:""})}><MIcon n="plus" s={10}/>Add note</button>
                              }
                              {ents.map(e=>{
                                const abs=isAbs(e.attendanceCode);
                                const eH=entryHrs(e);
                                const cc=OT_CODES.find(c=>c.value===e.otCode);
                                const blocks=[];
                                if(!abs){if(e.beforeStart&&e.beforeEnd)blocks.push([e.beforeStart,e.beforeEnd,"before"]);if(e.afterStart&&e.afterEnd)blocks.push([e.afterStart,e.afterEnd,"after"]);}
                                else if(e.duringStart&&e.duringEnd)blocks.push([e.duringStart,e.duringEnd,"during"]);
                                return (
                                  <div key={e.id} className={"dt-entry"+(abs?" k-absence":"")} onClick={()=>openEdit(d,e.id)} style={{cursor:"pointer"}}>
                                    <div className="dt-entry-top">
                                      <span className="typepill" style={{fontSize:11}}><span className="tp-dot" style={{background:abs?"var(--absence)":(e.attendanceCode==="0804"?"var(--standby)":"var(--accent)")}}/>{e.attendanceCode}</span>
                                      <span className="dt-entry-hrs">{fmtHrs(eH)}<i>h</i></span>
                                    </div>
                                    {blocks.map(([s,en],i)=>(
                                      <div key={i} className="dt-entry-block" style={{marginTop:3}}>
                                        <span className="dt-entry-time">{fmtTime(s)}<span className="dt-arr">→</span>{fmtTime(en)}</span>
                                      </div>
                                    ))}
                                    {!abs&&(
                                      <div className="dt-entry-tags">
                                        {cc&&<span className="dt-tag cc">{cc.label.split("—")[1]?.trim()}</span>}
                                        <span className={"dt-tag "+(e.compWageType==="1"?"banked":"paid")}>{e.compWageType==="1"?"Banked":"Paid"}</span>
                                        {e.orderProject&&<span className="dt-tag">#{e.orderProject}</span>}
                                      </div>
                                    )}
                                    {e.reason&&<div className="dt-entry-reason">"{e.reason}"</div>}
                                  </div>
                                );
                              })}
                              {xs.map(x=>(
                                <div key={x.id} className="dt-entry dt-exp-row" onClick={()=>openEditExpense(d,x.id)} style={{cursor:"pointer"}}>
                                  <div className="dt-entry-top">
                                    <span className="dt-exp-label"><span className="exp-ico"><MIcon n={EXP_TYPES[x.type]?.icon||"tag"} s={11}/></span>{EXP_TYPES[x.type]?.label||x.type}</span>
                                    <span className="dt-exp-row-amt">{money(x.amount)}</span>
                                  </div>
                                  {x.reason&&<div className="dt-entry-reason">"{x.reason}"</div>}
                                </div>
                              ))}
                              <div className="dt-add-row">
                                <button className="dt-add-btn ot" onClick={()=>openAdd(d)}><MIcon n="plus" s={11}/>OT</button>
                                <button className="dt-add-btn exp" onClick={()=>openAddExpense(d)}><MIcon n="plus" s={11}/>Expense</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {view==="current"&&!isDesktop&&(
        /* ══ MOBILE: original feed layout ══ */
        <>
          {/* ── Mobile feed: status row · stats · pp-nav · feed ── */}
          <div style={{maxWidth:448,margin:"0 auto"}}>

            {/* status row */}
            <div className="x-statusrow" style={{padding:"16px 18px 10px"}}>
              <span className="x-brandlet"><span className="x-logo-badge"><Logo size={22} /></span>OTTracker</span>
              <div className="x-statusrow-r">
                <SyncChip state={chipState} sub={chipSub} onClick={()=>user&&syncAll(user.id)} />
                <button className="x-gear" aria-label="Settings" onClick={()=>setView("settings")}><GearIcon size={18} /></button>
              </div>
            </div>

            {/* dual-ledger stats */}
            <div className="x-top">
              <div className="x-stat ot">
                <div className="x-stat-lab">OVERTIME</div>
                {standbyH>0 ? (
                  <div style={{display:"flex",gap:18,marginTop:2}}>
                    <div>
                      <div className="x-stat-val" style={{fontSize:22,lineHeight:1.05}}>{fmtHrs(otH)}<i>h</i></div>
                      <div className="x-stat-sub">OT</div>
                    </div>
                    <div>
                      <div className="x-stat-val" style={{fontSize:22,lineHeight:1.05,color:"var(--standby)"}}>{fmtHrs(standbyH)}<i>h</i></div>
                      <div className="x-stat-sub">Standby</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="x-stat-val">{fmtHrs(otH)}<i>h</i></div>
                    <div className="x-stat-sub">this pay period</div>
                  </>
                )}
              </div>
              <div className="x-stat-div" />
              <div className="x-stat exp">
                <div className="x-stat-lab">EXPENSES</div>
                <div className="x-stat-val">{money(expMonthTotal)}</div>
                <div className="x-stat-sub">{expMonthCount} claim{expMonthCount===1?"":"s"} · {expMonthLbl} · monthly</div>
              </div>
            </div>

            {/* pay-period navigator */}
            <div className="pp-nav">
              <button className="pp-arrow" aria-label="Previous pay period" onClick={()=>switchPeriod(stepPeriod(periodStart,-1))}>‹</button>
              <div className="pp-mid">
                <span className="pp-label">{periodRange(periodStart)}</span>
                {periodStart===getCurrentPeriodStart()
                  ? <span className="pp-current">Current</span>
                  : <button className="pp-current pp-jump" onClick={()=>switchPeriod(getCurrentPeriodStart())}>Jump to current</button>}
              </div>
              <button className="pp-arrow" aria-label="Next pay period" onClick={()=>switchPeriod(stepPeriod(periodStart,1))}>›</button>
            </div>

            <div className="c-feed">

              {/* filter pills */}
              <div className="c-pills">
                <button className={"c-pill"+(feedFilter==="all"?" on":"")} onClick={()=>setFeedFilter("all")}>All</button>
                <button className={"c-pill ot-pill"+(feedFilter==="ot"?" on":"")} onClick={()=>setFeedFilter("ot")}>Overtime</button>
                <button className={"c-pill x-pill"+(feedFilter==="expenses"?" on":"")} onClick={()=>setFeedFilter("expenses")}>Expenses</button>
                <button className={"c-pill notes-pill"+(feedFilter==="notes"?" on":"")} onClick={()=>setFeedFilter("notes")}>Notes</button>
                <button className={"c-pill abs-pill"+(feedFilter==="absence"?" on":"")} onClick={()=>setFeedFilter("absence")}>Absence</button>
              </div>

              {/* agenda day boxes (today pinned) */}
              {(()=>{
                const showOT    = feedFilter==="all"||feedFilter==="ot"||feedFilter==="absence";
                const showExp   = feedFilter==="all"||feedFilter==="expenses";
                const showNotes = feedFilter==="all"||feedFilter==="notes";
                const dayEnts=d=>showOT?(entries[d]||[]).filter(e=>feedFilter==="ot"?!isAbs(e.attendanceCode):feedFilter==="absence"?isAbs(e.attendanceCode):true):[];
                const dayExps=d=>showExp?(expenses[d]||[]):[];
                const dayNote=d=>dayNotes[d]&&dayNotes[d].note;
                const visible=(d,isToday)=>{
                  if(isToday) return true;
                  if(feedFilter==="notes") return !!dayNote(d);
                  if(feedFilter==="ot"||feedFilter==="absence") return dayEnts(d).length>0;
                  if(feedFilter==="expenses") return dayExps(d).length>0;
                  return dayEnts(d).length>0||dayExps(d).length>0||!!dayNote(d);
                };
                const dayCard=(d,isToday)=>{
                  if(!visible(d,isToday)) return null;
                  const ents=dayEnts(d), xs=dayExps(d), note=dayNote(d);
                  const lbl=dateLbl(d);
                  return (
                    <div className={"x-daybox"+(isToday?" is-today":"")} key={d}>
                      <div className="x-daybox-head">
                        {isToday&&<span className="x-today-badge">Today</span>}
                        <span className="x-dd-dow">{lbl.weekday}</span>
                        <span className="x-dd-num">{lbl.day}</span>
                        <span className="x-dd-mon">{lbl.month}</span>
                      </div>
                      {showNotes&&(note?(
                        <button className="x-daylog" onClick={()=>setNoteEdit({date:d,text:note})}>
                          <div className="x-daylog-lab">✎ Day note</div>
                          <div className="x-daylog-note">{note}</div>
                        </button>
                      ):(
                        <button className="x-daylog x-daylog-add" onClick={()=>setNoteEdit({date:d,text:""})}>+ Add a note for this day</button>
                      ))}
                      {(ents.length>0||xs.length>0)&&(
                        <div className="x-daybox-cards">
                          {ents.map(e=><FeedEntryCard key={e.id} e={e} onEdit={()=>openEdit(d,e.id)} onDelete={()=>requestDelete(d,e.id)} />)}
                          {xs.map(x=><ExpCard key={x.id} x={x} onEdit={()=>openEditExpense(d,x.id)} onDelete={()=>requestDeleteExp(d,x.id)} onReceipt={()=>x.receipt?setReceiptView({url:x.receipt,name:receiptFilename(x.type,x.date)}):openEditExpense(d,x.id)} />)}
                        </div>
                      )}
                    </div>
                  );
                };
                const inPeriod=dates.includes(today);
                const others=dates.filter(d=>d!==today);
                const hasOthers=others.some(d=>visible(d,false));
                const hasAny=inPeriod||dates.some(d=>visible(d,false));
                return (
                  <>
                    {inPeriod&&dayCard(today,true)}
                    {inPeriod&&hasOthers&&<div className="x-feed-divider">Rest of period</div>}
                    {(inPeriod?others:dates).map(d=>dayCard(d,false))}
                    {!hasAny&&(
                      <div style={{textAlign:"center",padding:"56px 24px",color:"var(--ink-3)"}}>
                        <div style={{fontSize:28,marginBottom:12,opacity:.5}}>○</div>
                        <div style={{fontSize:13,fontWeight:600}}>No entries this period</div>
                        <div style={{fontSize:12,marginTop:4}}>Use the buttons below to log overtime or add an expense.</div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* spacer so last card isn't hidden behind the fixed dock */}
              <div style={{height:104}} />
            </div>{/* /c-feed */}

          </div>{/* /mobile wrapper */}

          {/* fixed bottom dock — mobile only, hidden on desktop via .x-dock-wrap */}
          <div className="x-dock-wrap" style={{position:"fixed",left:0,right:0,bottom:0,zIndex:50,display:"flex",justifyContent:"center",pointerEvents:"none"}}>
            <div className="x-dock" style={{width:"100%",maxWidth:448,pointerEvents:"auto"}}>
              <button className="x-add exp" onClick={()=>openAddExpense(dates.includes(today)?today:periodStart)}><MIcon n="plus" s={18} />Add expense</button>
              <button className="x-add ot" onClick={()=>openAdd(today)}><MIcon n="plus" s={18} />Log overtime</button>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTORY ═══════════ */}
      {view==="history"&&(
        <div style={{maxWidth:760,margin:"0 auto",padding:"18px 16px 64px"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
              <button className="x-gear" aria-label="Back" style={{fontSize:20,lineHeight:1}} onClick={()=>setView(isDesktop?"current":"settings")}>‹</button>
              <div style={{flex:1}}>
                <h2 style={{fontSize:18,fontWeight:800,color:"var(--ink)"}}>Pay period history</h2>
                <p style={{fontSize:12,color:"var(--text2)",marginTop:2}}>Pay periods with entries, newest first. Tap one to open it.</p>
              </div>
            </div>
            {historyPeriods.length===0?(
              <div style={{textAlign:"center",padding:"48px 24px",color:"var(--text3)"}}>
                <div style={{fontSize:32,marginBottom:12}}>📋</div>
                <div style={{fontSize:14}}>No entries yet. Pay periods will appear here once you log overtime.</div>
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:16}}>
                {historyPeriods.map(p=>{
                  const hDates=getPeriodDates(p);
                  const hTot=hDates.reduce((s,d)=>s+dayHrs(d),0);
                  // Standby (0804) called out separately so it doesn't read as
                  // worked OT — same split the current-period dashboard shows.
                  const hStandbyH=hDates.reduce((s,d)=>s+(entries[d]||[]).filter(e=>e.attendanceCode==="0804").reduce((ss,e)=>ss+entryHrs(e),0),0);
                  const hOtH=hTot-hStandbyH;
                  const entryCount=hDates.reduce((s,d)=>s+(entries[d]||[]).length,0);
                  const isCur=p===getCurrentPeriodStart();
                  return (
                    <div key={p} className="hist-item" style={{cursor:"pointer"}}
                      onClick={()=>{ switchPeriod(p); setView("current"); }}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:15,fontWeight:600,color:"var(--text)",display:"flex",alignItems:"center",gap:8}}>
                            {periodRange(p)}
                            {isCur&&<span className="pp-current" style={{fontSize:10}}>Current</span>}
                          </div>
                          <div style={{fontSize:12,color:"var(--text2)",marginTop:3}}>
                            {`${entryCount} ${entryCount===1?"entry":"entries"}`}
                          </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:14}}>
                          {hStandbyH>0?(
                            <div style={{display:"flex",gap:14}}>
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:20,fontWeight:800,color:"var(--accent)",fontFamily:"var(--mono)",letterSpacing:"-.5px"}}>{fmtHrs(hOtH)}</div>
                                <div style={{fontSize:11,color:"var(--text3)"}}>OT hrs</div>
                              </div>
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:20,fontWeight:800,color:"var(--standby)",fontFamily:"var(--mono)",letterSpacing:"-.5px"}}>{fmtHrs(hStandbyH)}</div>
                                <div style={{fontSize:11,color:"var(--text3)"}}>Standby</div>
                              </div>
                            </div>
                          ):(
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:20,fontWeight:800,color:"var(--accent)",fontFamily:"var(--mono)",letterSpacing:"-.5px"}}>{fmtHrs(hTot)}</div>
                              <div style={{fontSize:11,color:"var(--text3)"}}>hrs</div>
                            </div>
                          )}
                          <span style={{color:"var(--ink-3)",fontSize:18,lineHeight:1}}>›</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      )}

      {/* ═══════════ TIME OFF BALANCES ═══════════ */}
      {view==="timeoff"&&(
        <div style={{maxWidth:520,margin:"0 auto",padding:"18px 16px 64px"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <button className="x-gear" aria-label="Back" style={{fontSize:20,lineHeight:1}} onClick={()=>{ setAcctEdit(null); setView("settings"); }}>‹</button>
            <div style={{flex:1}}>
              <h2 style={{fontSize:18,fontWeight:800,color:"var(--ink)"}}>Time off balances</h2>
              <p style={{fontSize:12,color:"var(--text2)",marginTop:2}}>Tap an account and copy its balance from ESS → Time Accounts. Entries logged after the set date adjust it automatically.</p>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {TIME_ACCOUNTS.map(a=>{
              const anchor=timeAccounts[a.key];
              const bal=accountBalance(a.key, anchor, entries);
              const balNeg=bal!=null&&Math.round(bal*10)/10<0;
              const editing=acctEdit&&acctEdit.account===a.key;
              return (
                <div key={a.key} className="hist-item" style={{cursor:editing?"default":"pointer"}}
                  onClick={()=>{ if(!editing) setAcctEdit({account:a.key, hours:anchor!=null&&anchor.hours!=null?String(anchor.hours):"", asOf:(anchor&&anchor.asOf)||localISODate()}); }}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:15,fontWeight:600,color:"var(--text)"}}>{a.label}</div>
                      <div style={{fontSize:12,color:"var(--text2)",marginTop:3}}>
                        {anchor?`Set to ${fmtHrs(anchor.hours)} hrs as of ${dateLbl(anchor.asOf).full}`:"Not set — tap to enter"}
                      </div>
                      {a.key==="banked"&&anchor&&(()=>{ const bd=bankedBreakdown(entries, anchor.asOf); return (
                        <div style={{fontSize:12,color:"var(--text2)",marginTop:3,fontFamily:"var(--mono)"}}>+{fmtHrs(bd.earned)} earned · −{fmtHrs(bd.used)} used since</div>
                      );})()}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:20,fontWeight:800,color:balNeg?"var(--fire)":"var(--accent)",fontFamily:"var(--mono)",letterSpacing:"-.5px"}}>{bal==null?"—":fmtHrs(bal)}</div>
                      <div style={{fontSize:11,color:"var(--text3)"}}>hrs left</div>
                    </div>
                  </div>
                  {editing&&(
                    <div onClick={e=>e.stopPropagation()} style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)",display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:6}}>Hours</div>
                        <input type="number" inputMode="decimal" min="0" step="0.25" placeholder="0.0" aria-label="Hours" value={acctEdit.hours}
                          onChange={e=>setAcctEdit(f=>({...f,hours:e.target.value}))}
                          style={{width:90,background:"var(--input-bg)",border:"1px solid var(--border2)",color:"var(--text)",padding:"8px 10px",borderRadius:8,fontFamily:"var(--mono)",fontSize:14,outline:"none",boxSizing:"border-box"}} />
                      </div>
                      <div>
                        <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:6}}>As of</div>
                        <input type="date" aria-label="As of date" value={acctEdit.asOf} max={localISODate()}
                          onChange={e=>setAcctEdit(f=>({...f,asOf:e.target.value}))}
                          style={{background:"var(--input-bg)",border:"1px solid var(--border2)",color:"var(--text)",padding:"8px 10px",borderRadius:8,fontFamily:"inherit",fontSize:13,outline:"none"}} />
                      </div>
                      <div style={{display:"flex",gap:8,marginLeft:"auto"}}>
                        <button className="btn-ghost" onClick={()=>setAcctEdit(null)}>Cancel</button>
                        <button className="btn-primary" disabled={!(parseFloat(acctEdit.hours)>=0)||!acctEdit.asOf} style={{opacity:(parseFloat(acctEdit.hours)>=0&&acctEdit.asOf)?1:.5}} onClick={saveTimeAccount}>Save</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══════════ SETTINGS ═══════════ */}
      {view==="settings"&&(
        <div style={{maxWidth:520,margin:"0 auto",padding:"18px 20px 64px"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:22}}>
            <button className="x-gear" aria-label="Back" style={{fontSize:20,lineHeight:1}} onClick={()=>setView("current")}>‹</button>
            <h2 style={{fontSize:18,fontWeight:800,color:"var(--ink)"}}>Settings</h2>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:16}}>

            {/* Theme card */}
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:12,padding:"20px 24px"}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",letterSpacing:".1em",textTransform:"uppercase",marginBottom:20,paddingBottom:10,borderBottom:"1px solid var(--border)"}}>Appearance</div>
              <div style={{display:"flex",gap:8}}>
                {[["system","System"],["light","Light"],["dark","Dark"]].map(([val,lbl])=>(
                  <button key={val} onClick={()=>changeTheme(val)}
                    style={{flex:1,padding:"10px 8px",borderRadius:8,border:theme===val?"2px solid var(--text)":"1px solid var(--border2)",background:theme===val?"var(--surface2)":"transparent",color:theme===val?"var(--text)":"var(--text2)",fontFamily:"inherit",fontSize:13,fontWeight:theme===val?600:400,cursor:"pointer",transition:"all .15s"}}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            {/* Records & data card */}
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:12,padding:"20px 24px"}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",letterSpacing:".1em",textTransform:"uppercase",marginBottom:18,paddingBottom:10,borderBottom:"1px solid var(--border)"}}>Records &amp; data</div>
              <button onClick={()=>setView("history")}
                style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--surface2)",border:"1px solid var(--border2)",borderRadius:10,padding:"12px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:14,color:"var(--text)",marginBottom:12}}>
                <span style={{fontWeight:600}}>Pay period history</span>
                <span style={{color:"var(--text3)",fontSize:13}}>{historyPeriods.length>0?`${historyPeriods.length} period${historyPeriods.length===1?"":"s"}`:"—"} ›</span>
              </button>
              <button onClick={()=>setView("timeoff")}
                style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--surface2)",border:"1px solid var(--border2)",borderRadius:10,padding:"12px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:14,color:"var(--text)",marginBottom:12}}>
                <span style={{fontWeight:600}}>Time off balances</span>
                <span style={{color:"var(--text3)",fontSize:13}}>
                  {(()=>{ const b=accountBalance("banked", timeAccounts.banked, entries); return b==null?"—":`${fmtHrs(b)} banked`; })()} ›
                </span>
              </button>
              <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>Export the current period ({periodRange(periodStart)})</div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <button className="export-btn" onClick={()=>exportExcel()}>Export Excel</button>
                <button className="export-btn" onClick={()=>setShowESS(periodStart)}>ESS view</button>
                <button className="export-btn" onClick={()=>setShowESSExp({month:monthKey(localISODate()),cc:"62"})}>Resources ESS</button>
                <button className="export-btn" onClick={()=>setShowESSExp({month:monthKey(localISODate()),cc:"60"})}>Fire Control ESS</button>
                <button className="export-btn" onClick={()=>setShowPrint(periodStart)}>Print view</button>
                {exportMsg&&<span style={{fontSize:11,color:"var(--ink-2)"}}>{exportMsg}</span>}
              </div>
              <div style={{fontSize:11,color:"var(--text3)",margin:"14px 0 8px"}}>Back up or restore all your data (entries, expenses &amp; notes)</div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <button className="export-btn" onClick={exportJSON}>Download data (JSON)</button>
                <label className="export-btn" style={{cursor:"pointer"}}>Import data (JSON)
                  <input type="file" accept="application/json,.json" hidden onChange={e=>{ importJSON(e.target.files&&e.target.files[0]); e.target.value=""; }} />
                </label>
              </div>
            </div>

            {/* Employee Info card */}
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:12,padding:"20px 24px"}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",letterSpacing:".1em",textTransform:"uppercase",marginBottom:20,paddingBottom:10,borderBottom:"1px solid var(--border)"}}>Employee Info</div>
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {[["Full Name","name","e.g. John Smith"],["Employee ID","employeeId","e.g. 12345"],["Department","department","e.g. Forestry"]].map(([label,key,ph])=>(
                  <div key={key}>
                    <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:6}}>{label}</div>
                    <input type="text" placeholder={ph} value={empForm[key]}
                      onChange={e=>setEmpForm(f=>({...f,[key]:e.target.value}))}
                      style={{width:"100%",background:"var(--input-bg)",border:"1px solid var(--border2)",color:"var(--text)",padding:"9px 12px",borderRadius:8,fontFamily:"inherit",fontSize:14,outline:"none",boxSizing:"border-box"}} />
                  </div>
                ))}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4,flexWrap:"wrap",gap:8}}>
                  <button className="btn-primary" onClick={()=>{ saveEmployee(empForm); setEmployee(empForm); if(pb.authStore.isValid) pbSaveEmployee(empForm, pb.authStore.model.id); }}>Save Info</button>
                  {employee.name&&<div style={{fontSize:11,color:"var(--text2)"}}>✓ Saved</div>}
                </div>
                {employee.name&&(
                  <div style={{background:"var(--surface2)",borderRadius:8,padding:"10px 12px",fontSize:12,color:"var(--text2)",lineHeight:1.7}}>
                    <strong style={{color:"var(--text)"}}>{employee.name}</strong><br/>
                    ID: {employee.employeeId}<br/>
                    Dept: {employee.department}
                  </div>
                )}
              </div>
            </div>

            {/* Account card */}
            <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:12,padding:"20px 24px"}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",letterSpacing:".1em",textTransform:"uppercase",marginBottom:18,paddingBottom:10,borderBottom:"1px solid var(--border)"}}>Account</div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                <div style={{fontSize:13,color:"var(--text2)"}}>{user&&(user.email||user.username)}</div>
                <button className="btn-ghost" onClick={logout} style={{color:"var(--fire)",borderColor:"color-mix(in oklab,var(--fire) 40%,var(--border2))"}}>Sign out</button>
              </div>
            </div>

            {isAdmin(user) && <AdminUsers />}

          </div>
        </div>
      )}

      {/* ── PRINT VIEW MODAL ── */}
      {showPrint&&(()=>{
        const pDates=getPeriodDates(showPrint);
        const pEntries=entries; // single live map keyed by date; print filters by pDates
        const pw1=pDates.slice(0,7), pw2=pDates.slice(7,14);
        const pw1H=pw1.reduce((s,d)=>s+dayHrs(d,pEntries),0);
        const pw2H=pw2.reduce((s,d)=>s+dayHrs(d,pEntries),0);
        const pTotH=pw1H+pw2H;
        const allRows=[];
        pDates.forEach(date=>{
          (pEntries[date]||[]).forEach(e=>{
            const abs=isAbs(e.attendanceCode);
            const cc=OT_CODES.find(c=>c.value===e.otCode)?.costCentre||"";
            const att=ATTENDANCE_CODES.find(c=>c.value===e.attendanceCode);
            const cw=COMP_WAGE_TYPES.find(c=>c.value===e.compWageType);
            const bm=calcMins(e.beforeStart,e.beforeEnd), am=calcMins(e.afterStart,e.afterEnd), dm=calcMins(e.duringStart,e.duringEnd);
            const startT=abs?e.duringStart:(e.beforeStart||e.afterStart);
            const endT=abs?e.duringEnd:(e.afterEnd||e.beforeEnd);
            allRows.push({date,att,cw,cc,abs,startT,endT,hrs:entryHrs(e),reason:e.reason,orderProject:e.orderProject,bm,am,dm,e});
          });
        });
        return (
          <div className="modal-bg" onClick={e=>{if(e.target===e.currentTarget)setShowPrint(null);}}>
            <div style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:14,padding:0,width:"100%",maxWidth:640,maxHeight:"92vh",overflow:"auto",boxShadow:"var(--shadow-modal)"}}>
              {/* Print header */}
              <div style={{padding:"12px 12px 12px 20px",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:"var(--text)"}}>Pay Period Summary</div>
                  <div style={{fontSize:12,color:"var(--text2)",marginTop:2}}>{periodRange(showPrint)}</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button className="export-btn" onClick={()=>window.print()}>Print</button>
                  <button className="btn-icon" style={{fontSize:18,lineHeight:1,padding:"4px 6px"}} onClick={()=>setShowPrint(null)}>×</button>
                </div>
              </div>

              <div style={{padding:"20px 24px"}} id="print-area">
                {/* Employee info */}
                {(employee.name||employee.employeeId||employee.department)&&(
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20,padding:"12px 16px",background:"var(--surface)",borderRadius:8,border:"1px solid var(--border)"}}>
                    {employee.name&&<div><div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>Name</div><div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{employee.name}</div></div>}
                    {employee.employeeId&&<div><div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>Employee ID</div><div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{employee.employeeId}</div></div>}
                    {employee.department&&<div><div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>Department</div><div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{employee.department}</div></div>}
                  </div>
                )}

                {/* Totals */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
                  {[["Total Hours",fmtHrs(pTotH)],["Week 1",fmtHrs(pw1H)],["Week 2",fmtHrs(pw2H)]].map(([l,v])=>(
                    <div key={l} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px"}}>
                      <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>{l}</div>
                      <div style={{fontSize:22,fontWeight:700,color:"var(--text)"}}>{v} <span style={{fontSize:12,fontWeight:400,color:"var(--text2)"}}>hrs</span></div>
                    </div>
                  ))}
                </div>

                {/* Entries table */}
                {allRows.length===0?(
                  <div style={{textAlign:"center",padding:"32px",color:"var(--text3)",fontSize:13}}>No entries for this period.</div>
                ):(
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{borderBottom:"2px solid var(--border)"}}>
                        {["Date","Code","Type","Cost Centre","Time","Hours","Reason"].map(h=>(
                          <th key={h} style={{textAlign:"left",padding:"6px 8px",fontSize:10,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:".06em"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allRows.map((r,i)=>(
                        <tr key={i} style={{borderBottom:"1px solid var(--border)",background:i%2===0?"transparent":"var(--surface)"}}>
                          <td style={{padding:"7px 8px",color:"var(--text)",whiteSpace:"nowrap"}}>{r.date}</td>
                          <td style={{padding:"7px 8px"}}><span className={`tag tag-${r.e.attendanceCode}`}>{r.e.attendanceCode}</span></td>
                          <td style={{padding:"7px 8px",color:"var(--text2)",fontSize:11}}>{r.abs?"During":(r.bm>0&&r.am>0?"Before+After":r.bm>0?"Before":"After")}</td>
                          <td style={{padding:"7px 8px",color:"var(--text2)"}}>{r.abs?"—":r.cc}</td>
                          <td style={{padding:"7px 8px",color:"var(--text)",whiteSpace:"nowrap"}}>{fmtTime(r.startT)}–{fmtTime(r.endT)}</td>
                          <td style={{padding:"7px 8px",fontWeight:600,color:"var(--text)",whiteSpace:"nowrap"}}>{fmtHrs(r.hrs)}</td>
                          <td style={{padding:"7px 8px",color:"var(--text2)",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.reason||"—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{borderTop:"2px solid var(--border)"}}>
                        <td colSpan={5} style={{padding:"8px",fontWeight:700,fontSize:12,color:"var(--text)"}}>Total</td>
                        <td style={{padding:"8px",fontWeight:700,fontSize:14,color:"var(--text)"}}>{fmtHrs(pTotH)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ESS weekly grid helper — grouped rows to transcribe into SAP ESS */}
      {showESS&&(()=>{
        const pDates=getPeriodDates(showESS);
        const weeks=[pDates.slice(0,7), pDates.slice(7,14)];
        const buildWeek=(wkDates)=>{
          const groups={};
          wkDates.forEach((date,di)=>{
            (entries[date]||[]).forEach(e=>{
              const cc=OT_CODES.find(c=>c.value===e.otCode)?.costCentre||"—";
              const code=e.attendanceCode||"0801";
              const comp=e.compWageType||"0";
              const key=cc+"|"+code+"|"+comp;
              if(!groups[key]) groups[key]={cctr:cc,code,comp,days:[0,0,0,0,0,0,0],total:0};
              const h=entryHrs(e); groups[key].days[di]+=h; groups[key].total+=h;
            });
          });
          return Object.values(groups).sort((a,b)=>a.code.localeCompare(b.code)||a.cctr.localeCompare(b.cctr));
        };
        const dot=(code)=> code==="0804"?"var(--standby)":(isAbs(code)?"var(--absence)":"var(--accent)");
        const ddmmyyyy=(iso)=>{const d=new Date(iso+"T00:00:00");return String(d.getDate()).padStart(2,"0")+"."+String(d.getMonth()+1).padStart(2,"0")+"."+d.getFullYear();};
        const wd=["Su","Mo","Tu","We","Th","Fr","Sa"];
        const mono={fontFamily:"'JetBrains Mono',monospace"};
        return (
          <div className="modal-bg" onClick={e=>{if(e.target===e.currentTarget)setShowESS(null);}}>
            <div style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:14,padding:0,width:"100%",maxWidth:660,maxHeight:"92vh",overflow:"auto",boxShadow:"var(--shadow-modal)"}}>
              <div style={{padding:"12px 12px 12px 20px",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:"var(--text)"}}>ESS weekly grid</div>
                  <div style={{fontSize:12,color:"var(--text2)",marginTop:2}}>{periodRange(showESS)}</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button className="export-btn" onClick={()=>window.print()}>Print</button>
                  <button className="btn-icon" style={{fontSize:18,lineHeight:1,padding:"4px 6px"}} onClick={()=>setShowESS(null)}>×</button>
                </div>
              </div>
              <div style={{padding:"16px 20px"}} id="ess-print-area">
                {weeks.map((wkDates,wi)=>{
                  const rows=buildWeek(wkDates);
                  return (
                    <div key={wi} style={{marginBottom:wi===0?20:0}}>
                      <div style={{fontSize:12,fontWeight:600,color:"var(--text2)",margin:"0 2px 6px"}}>Week {wi+1} · Week From: {ddmmyyyy(wkDates[0])}</div>
                      <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed",fontSize:12.5}}>
                          <colgroup><col style={{width:56}}/><col style={{width:120}}/><col style={{width:40}}/><col span={7}/><col style={{width:42}}/></colgroup>
                          <thead>
                            <tr style={{color:"var(--text2)",fontSize:11}}>
                              <th style={{textAlign:"left",padding:"7px 6px",fontWeight:600}}>CCtr</th>
                              <th style={{textAlign:"left",padding:"7px 6px",fontWeight:600}}>Att/abs type</th>
                              <th style={{textAlign:"center",padding:"7px 2px",fontWeight:600}}>Comp</th>
                              {wkDates.map((d,i)=>(<th key={i} style={{textAlign:"center",padding:"7px 1px",fontWeight:600}}>{wd[i]}<br/>{new Date(d+"T00:00:00").getDate()}</th>))}
                              <th style={{textAlign:"right",padding:"7px 6px",fontWeight:600}}>Tot</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length===0?(
                              <tr><td colSpan={11} style={{textAlign:"center",padding:"18px",color:"var(--text3)",fontSize:12}}>No entries this week.</td></tr>
                            ):rows.map((r,ri)=>(
                              <tr key={ri} style={{borderTop:"1px solid var(--border)"}}>
                                <td style={{padding:"8px 6px",color:"var(--text)",...mono}}>{r.cctr}</td>
                                <td style={{padding:"8px 6px",color:"var(--text)"}}><span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:dot(r.code),marginRight:6}}/>{SHORT_CODE[r.code]||r.code} · {r.code}</td>
                                <td style={{textAlign:"center",padding:"8px 2px",color:"var(--text)",...mono}}>{r.comp}</td>
                                {r.days.map((h,di)=>(<td key={di} style={{textAlign:"center",color:h>0?"var(--text)":"var(--text3)",...mono}}>{h>0?fmtHrs(h):"–"}</td>))}
                                <td style={{textAlign:"right",padding:"8px 6px",fontWeight:600,color:"var(--text)",...mono}}>{fmtHrs(r.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ESS Expenses helper — redesigned for ESS input order: General Data → Comments → per-receipt cards */}
      {showESSExp&&(()=>{
        const {month:essMonth, cc:essCC} = showESSExp;
        const ccName   = essCC==="60" ? "Fire Control" : "Resources";
        const ccNum    = essCC==="60" ? "160060" : "160062";
        const ccColor  = essCC==="60" ? "var(--fire)" : "var(--res)";
        const monthExps=allExpenses.filter(x=>monthKey(x.date)===essMonth && (x.costCentre||"62")===essCC);
        const rows=buildESSExpenseRows(monthExps);
        const claimRows=rows.filter(r=>!r.isMileage);
        const mileageRows=rows.filter(r=>r.isMileage);
        const total=claimRows.reduce((s,r)=>s+r.amount,0);
        const ddmmyyyy=(iso)=>essDate(iso)||"—";
        const mono={fontFamily:"'JetBrains Mono',monospace"};
        const reasonText=`${monthLabelLong(essMonth)} expenses (${ccName})`;
        const essCommentsKey=`${essMonth}|${essCC}`;
        // The Comments box shows each day's reason, laid out by date, until the
        // user types in it; from the first keystroke their own text is what's
        // stored and shown (an edited box is never silently overwritten when a
        // later expense is added — "Rebuild" does that, on request).
        // undefined = untouched, "" = deliberately cleared.
        const commentsDraft=buildEssComments(rows);
        const savedComments=essComments[essCommentsKey];
        const commentsText=savedComments!==undefined?savedComments:commentsDraft;
        const commentsEdited=savedComments!==undefined&&savedComments!==commentsDraft;
        const copy=(key,text)=>{
          navigator.clipboard.writeText(text).catch(()=>{});
          setEssCopied(key);
          setTimeout(()=>setEssCopied(k=>k===key?null:k),1800);
        };
        const cpBtn=(key,text)=>(
          <button onClick={()=>copy(key,text)} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11.5,fontWeight:700,flexShrink:0,
            color:essCopied===key?"oklch(0.46 0.14 162)":"var(--accent)",
            background:essCopied===key?"color-mix(in oklab,oklch(0.46 0.14 162) 9%,var(--surface))":"color-mix(in oklab,var(--accent) 9%,var(--surface))",
            border:"1px solid",borderColor:essCopied===key?"color-mix(in oklab,oklch(0.46 0.14 162) 24%,var(--line))":"color-mix(in oklab,var(--accent) 22%,var(--line))",
            borderRadius:7,padding:"3px 9px",cursor:"pointer",whiteSpace:"nowrap"}}>
            {essCopied===key?"✓ Copied":"⎘ Copy"}
          </button>
        );
        const card={background:"var(--surface)",border:"1px solid var(--line-soft)",borderRadius:14,overflow:"hidden"};
        const secHead={fontSize:11.5,fontWeight:700,letterSpacing:".6px",textTransform:"uppercase",color:"var(--ink-3)",marginBottom:8};
        const gdRow=(lab,val,cpKey)=>(
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 14px",borderBottom:"1px solid var(--line-soft)",gap:12}}>
            <span style={{fontSize:12.5,color:"var(--ink-3)",flexShrink:0}}>{lab}</span>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{...mono,fontSize:12.5,fontWeight:600,color:"var(--ink)"}}>{val}</span>
              {cpKey&&cpBtn(cpKey,val)}
            </div>
          </div>
        );
        return (
          <div className="modal-bg" onClick={e=>{if(e.target===e.currentTarget)setShowESSExp(null);}}>
            <div style={{background:"var(--bg)",border:"1px solid var(--line)",borderRadius:"var(--radius)",width:"100%",maxWidth:600,maxHeight:"92vh",overflow:"auto",boxShadow:"var(--shadow-modal)"}}>

              {/* Header */}
              <div style={{padding:"16px 16px 14px 20px",borderBottom:"1px solid var(--line-soft)",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:17,fontWeight:800,letterSpacing:"-.3px",color:"var(--ink)"}}>ESS Expenses</div>
                  <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11.5,fontWeight:700,color:ccColor,
                    background:`color-mix(in oklab,${ccColor} 10%,var(--surface))`,
                    border:`1px solid color-mix(in oklab,${ccColor} 24%,var(--line))`,
                    borderRadius:8,padding:"3px 9px",marginTop:5}}>
                    <span style={{width:6,height:6,borderRadius:"50%",background:ccColor,display:"inline-block",flexShrink:0}}/>
                    {ccName} · {ccNum}
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <button className="btn-icon" style={{width:28,height:28,borderRadius:9,fontSize:15,lineHeight:1}} onClick={()=>setShowESSExp({month:stepMonth(essMonth,-1),cc:essCC})}>‹</button>
                    <span style={{fontSize:13,fontWeight:700,color:"var(--ink-2)",minWidth:78,textAlign:"center"}}>{monthLabelLong(essMonth)}</span>
                    <button className="btn-icon" style={{width:28,height:28,borderRadius:9,fontSize:15,lineHeight:1}} onClick={()=>setShowESSExp({month:stepMonth(essMonth,1),cc:essCC})}>›</button>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button className="export-btn" onClick={()=>window.print()}>Print</button>
                    <button className="btn-icon" style={{fontSize:18,lineHeight:1,padding:"4px 6px"}} onClick={()=>setShowESSExp(null)}>×</button>
                  </div>
                </div>
              </div>

              <div style={{padding:"18px 20px",display:"flex",flexDirection:"column",gap:16}} id="ess-exp-print-area">

                {/* Print-only heading */}
                <div className="ess-print-title">{monthLabelLong(essMonth)} — ESS Expenses — {ccName}</div>

                {/* General Data */}
                <div>
                  <div style={secHead}>General Data</div>
                  <div style={card}>
                    {gdRow("Trip type","In Province Travel",null)}
                    {gdRow("Start date",ddmmyyyy(monthFirstISO(essMonth)),"gd-start")}
                    {gdRow("End date",ddmmyyyy(monthLastISO(essMonth)),"gd-end")}
                    {gdRow("Destination","Various","gd-dest")}
                    {gdRow("Reason",reasonText,"gd-reason")}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 14px",gap:12}}>
                      <span style={{fontSize:12.5,color:"var(--ink-3)",flexShrink:0}}>Account assignment</span>
                      <span style={{...mono,fontSize:12.5,fontWeight:600,color:"var(--ink)"}}>Cost Center {ccNum}</span>
                    </div>
                  </div>
                </div>

                {/* Comments — each day's reason laid out by date, editable and persisted per (month, cost centre) */}
                <div>
                  <div style={secHead}>Comments</div>
                  <div style={card}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderBottom:"1px solid var(--line-soft)",gap:10}}>
                      <span style={{fontSize:12.5,fontWeight:700,color:"var(--ink-2)"}}>Paste into Comments on General Data</span>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        {commentsEdited&&commentsDraft&&(
                          <button onClick={()=>setEssComments(prev=>({...prev,[essCommentsKey]:commentsDraft}))}
                            style={{fontSize:11.5,fontWeight:700,flexShrink:0,color:"var(--ink-2)",background:"var(--surface-2)",
                              border:"1px solid var(--line)",borderRadius:7,padding:"3px 9px",cursor:"pointer",whiteSpace:"nowrap"}}>
                            ↻ Rebuild
                          </button>
                        )}
                        {commentsText.trim()&&cpBtn("comments-all",commentsText)}
                      </div>
                    </div>
                    {/* Grows with the draft — a busy month is one line per day, and
                        the CSS min-height alone clips them. Capped so a 30-day
                        month scrolls inside the box instead of burying the
                        receipt cards below it. */}
                    <textarea
                      className="f-reason-inp"
                      rows={Math.min(16,Math.max(4,commentsText.split("\n").length+1))}
                      placeholder={'One line per day, from the reason written on that day\'s expenses. Add a reason to your expenses to fill this in — or type your own summary here.'}
                      value={commentsText}
                      onChange={e=>setEssComments(prev=>({...prev,[essCommentsKey]:e.target.value}))}
                      style={{padding:"12px 14px",...mono,fontSize:12,lineHeight:1.8}}
                    />
                    <div style={{padding:"0 14px 10px",fontSize:11,color:"var(--ink-3)",lineHeight:1.5}}>
                      {commentsEdited?"Edited — your text is kept as you typed it. Rebuild replaces it with the day-by-day reasons.":"Each day's reason, by date. Type here to edit — your changes are kept."}
                    </div>
                  </div>
                </div>

                {/* Enter Receipts — one card per expense */}
                <div>
                  <div style={secHead}>Enter Receipts</div>
                  {claimRows.length===0
                    ?<div style={{...card,padding:"18px",textAlign:"center",fontSize:13,color:"var(--ink-3)"}}>No expenses this month.</div>
                    :<div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {claimRows.map((r,ri)=>(
                        <div key={ri} style={card}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px",background:"var(--surface-2)",borderBottom:"1px solid var(--line-soft)"}}>
                            <div>
                              <div style={{fontSize:13.5,fontWeight:700,color:"var(--ink)"}}>{r.label}</div>
                              <div style={{...mono,fontSize:11.5,color:"var(--ink-3)",marginTop:1}}>{ddmmyyyy(r.date)}</div>
                            </div>
                            <div style={{...mono,fontSize:13.5,fontWeight:700,color:"var(--ink)"}}>{money(r.amount)}</div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",borderBottom:"1px solid var(--line-soft)",gap:10}}>
                            <span style={{fontSize:11.5,fontWeight:700,textTransform:"uppercase",letterSpacing:".4px",color:"var(--ink-3)",flexShrink:0,minWidth:80}}>Location</span>
                            <span style={{...mono,fontSize:12,color:r.location?"var(--ink)":"var(--ink-3)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.location||"—"}</span>
                            {r.location&&cpBtn(`loc-${ri}`,r.location)}
                          </div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",gap:10}}>
                            <span style={{fontSize:11.5,fontWeight:700,textTransform:"uppercase",letterSpacing:".4px",color:"var(--ink-3)",flexShrink:0,minWidth:80}}>Receipt</span>
                            {r.hasReceipt
                              ?<button className="x-rbtn" style={{fontSize:12,padding:"5px 11px"}} onClick={()=>downloadDataUrl(r.receipt,receiptFilename(r.type,r.date))}>⬇ Download</button>
                              :<span style={{...mono,fontSize:12,color:"var(--ink-3)"}}>— none required (per diem)</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                </div>

                {/* Total */}
                {claimRows.length>0&&(
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    background:`color-mix(in oklab,${ccColor} 8%,var(--surface))`,
                    border:`1px solid color-mix(in oklab,${ccColor} 20%,var(--line))`,
                    borderRadius:14,padding:"12px 16px"}}>
                    <span style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>Total</span>
                    <span style={{...mono,fontSize:19,fontWeight:700,color:ccColor,letterSpacing:"-.5px"}}>{money(total)}</span>
                  </div>
                )}

                {/* Mileage note */}
                {mileageRows.length>0&&(
                  <div style={{padding:"10px 12px",border:"1px dashed var(--line)",borderRadius:10,fontSize:12,color:"var(--ink-2)"}}>
                    <b style={{color:"var(--ink)"}}>Mileage ({mileageRows.length})</b> — enter in ESS under <b>Enter Mileage Details</b>, not Enter Receipts:
                    <div style={{marginTop:6,...mono,color:"var(--ink)"}}>{mileageRows.map(r=>[ddmmyyyy(r.date),money(r.amount),r.location,r.reason].filter(Boolean).join(" · ")).join("  |  ")}</div>
                  </div>
                )}

                {/* Steps */}
                <div>
                  <div style={secHead}>How to enter</div>
                  <div style={{...card,padding:"12px 14px"}}>
                    <ol style={{margin:0,paddingLeft:18,display:"flex",flexDirection:"column",gap:5}}>
                      {[
                        <span>ESS Overview → <b>Create Expense Claim</b>.</span>,
                        <span>Select Trip Type: <b>In Province Travel</b> → Start.</span>,
                        "General Data: fill Start/End date, Destination, Reason, paste Comments — copy buttons above.",
                        <span><b>Enter Receipts</b> → New Entry for each card above.</span>,
                        "Per entry: pick Expense Type, set Amount and Date, paste Location.",
                        <span>Save (or Save and New Entry), then Review → <b>Save and Send for Approval</b>.</span>,
                        "If receipts required: Display Expense Form, print, attach originals, mail to PTS.",
                      ].map((s,i)=><li key={i} style={{fontSize:12.5,color:"var(--ink-2)",lineHeight:1.5}}>{s}</li>)}
                    </ol>
                  </div>
                </div>

              </div>
            </div>
          </div>
        );
      })()}

      {/* Log overtime / absence — full-screen form sheet */}
      {showForm&&(
        <div className="a-sheet">
          <div className="a-form-col">
            <div className="a-form-bar">
              <button className="a-fbtn" onClick={()=>setShowForm(false)}>Cancel</button>
              <span className="a-ftitle">{editId?"Edit entry":"Log overtime"}</span>
              <button className="a-fbtn save" disabled={!fTot} style={{opacity:fTot?1:.4}} onClick={saveEntry}>Save</button>
            </div>

            <div className="scr-body a-form">
              {!editId&&lastEntry&&(
                <button className="quickfill" onClick={()=>setForm({...lastEntry})}>
                  <span className="qf-ico" style={{fontSize:15}}>↺</span>
                  <span className="qf-txt">
                    <b>Fill from last entry</b>
                    <span>{ATTENDANCE_CODES.find(c=>c.value===lastEntry.attendanceCode)?.label?.split(" — ")[1]||""} · {lastEntry.orderProject||"no project"} · {lastEntry.reason?.slice(0,26)||""}</span>
                  </span>
                  <span style={{color:"var(--ink-3)",fontSize:16}}>›</span>
                </button>
              )}

              <div className="f-sec">Attendance code</div>
              <div className="f-types coded twocol">
                <div className="ft-col">
                  {ATTENDANCE_CODES.filter(c=>c.type==="absence").map(c=>(
                    <button key={c.value} className={"ftype k-absence"+(form.attendanceCode===c.value?" on":"")} onClick={()=>setForm(f=>({...f,attendanceCode:c.value}))}>
                      <span className="tp-dot" />{SHORT_CODE[c.value]}<i className="ft-code">{c.value}</i>
                    </button>
                  ))}
                </div>
                <div className="ft-col">
                  {ATTENDANCE_CODES.filter(c=>c.type==="ot").map(c=>(
                    <button key={c.value} className={"ftype"+(form.attendanceCode===c.value?" on":"")} onClick={()=>setForm(f=>({...f,attendanceCode:c.value}))}>
                      <span className="tp-dot" />{SHORT_CODE[c.value]}<i className="ft-code">{c.value}</i>
                    </button>
                  ))}
                </div>
              </div>
              {(()=>{ const otherOn=OTHER_CODES.some(c=>c.value===form.attendanceCode); return (
                <div className={"f-card f-other"+(otherOn?" on":"")}>
                  <div className="f-row f-center" style={{position:"relative"}}>
                    <span className={"f-val f-date"+(otherOn?"":" ph")}>
                      {otherOn ? OTHER_CODES.find(c=>c.value===form.attendanceCode)?.label : "Other codes"}
                    </span>
                    <span aria-hidden="true" className="f-other-arrow">▾</span>
                    <select
                      className="f-other-sel"
                      aria-label="Other codes"
                      value={otherOn?form.attendanceCode:""}
                      onChange={e=>e.target.value&&setForm(f=>({...f,attendanceCode:e.target.value}))}
                    >
                      <option value="">Other codes…</option>
                      {OTHER_CODES.map(c=>(<option key={c.value} value={c.value}>{c.label}</option>))}
                    </select>
                  </div>
                </div>
              ); })()}

              <div className="f-sec">When</div>
              <div className="f-card">
                <div className="f-row f-center" style={{position:"relative"}}>
                  <span className="f-val f-date">{selDate&&dateLbl(selDate).full}</span>
                  <span aria-hidden="true" style={{marginLeft:8,color:"var(--ink-3)",fontSize:14}}>▾</span>
                  <input
                    type="date"
                    aria-label="Entry date"
                    value={selDate||""}
                    min={periodStart}
                    max={getPeriodEnd(periodStart)}
                    onChange={(e)=>{ if(e.target.value) setSelDate(e.target.value); }}
                    onClick={(e)=>{ try{ e.currentTarget.showPicker(); }catch(_){} }}
                    style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",opacity:0,border:"none",padding:0,margin:0,cursor:"pointer",WebkitAppearance:"none"}}
                  />
                </div>
              </div>

              <div className="f-sec">Times</div>
              <div className="f-card" style={{padding:"4px 0"}}>
                {!fAbs?(
                  [["Before shift","beforeStart","beforeEnd",fBm],["After shift","afterStart","afterEnd",fAm]].map(([lab,sk,ek,dur],i)=>(
                    <div key={lab} style={{padding:"12px 16px",borderBottom:i===0?"1px solid var(--line-soft)":"none"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
                        <span style={{fontSize:13,fontWeight:600,color:"var(--ink-2)"}}>{lab}</span>
                        {dur>0&&<span style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:600,color:"var(--ink-3)"}}>{fmtDur(dur)}</span>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:9}}>
                        <select className="f-time-sel" value={form[sk]} onChange={e=>setForm(f=>({...f,[sk]:e.target.value}))}><option value="">—</option>{TIME_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                        <span className="f-arrow">→</span>
                        <select className="f-time-sel" value={form[ek]} onChange={e=>setForm(f=>({...f,[ek]:e.target.value}))}><option value="">—</option>{TIME_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                      </div>
                    </div>
                  ))
                ):(
                  <div style={{padding:"12px 16px"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
                      <span style={{fontSize:13,fontWeight:600,color:"var(--ink-2)"}}>During shift</span>
                      {fDm>0&&<span style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:600,color:"var(--ink-3)"}}>{fmtDur(fDm)}</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                      <select className="f-time-sel" value={form.duringStart} onChange={e=>setForm(f=>({...f,duringStart:e.target.value}))}><option value="">—</option>{TIME_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                      <span className="f-arrow">→</span>
                      <select className="f-time-sel" value={form.duringEnd} onChange={e=>setForm(f=>({...f,duringEnd:e.target.value}))}><option value="">—</option>{TIME_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                    </div>
                  </div>
                )}
              </div>

              {!fAbs&&(
                <>
                  <div className="f-sec">Overtime details</div>
                  <div className="f-card">
                    <div className="f-row">
                      <span className="f-lab">Comp / wage type</span>
                      <div className="f-seg sm">
                        {COMP_WAGE_TYPES.map(c=>{
                          const noBank = c.value==="1" && form.otCode==="60";
                          return (
                            <button key={c.value} disabled={noBank}
                              className={"seg"+(form.compWageType===c.value?" on":"")}
                              style={noBank?{opacity:.4,cursor:"not-allowed"}:undefined}
                              onClick={()=>setForm(f=>({...f, compWageType:c.value,
                                doubleOt: c.value==="1" ? suggestDoubleOt(selDate, f.attendanceCode) : false}))}>
                              {(c.label.split("— ")[1]||c.label).trim()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {form.compWageType==="1"&&(
                      <div className="f-row">
                        <span className="f-lab">Banked rate</span>
                        <div className="f-seg sm">
                          <button className={"seg"+(!form.doubleOt?" on":"")} onClick={()=>setForm(f=>({...f,doubleOt:false}))}>
                            {form.attendanceCode==="0804"?"⅓×":"1.5×"}
                          </button>
                          <button className={"seg"+(form.doubleOt?" on":"")} onClick={()=>setForm(f=>({...f,doubleOt:true}))}>
                            2× rest/stat
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="f-row col">
                      <span className="f-lab">OT code · cost centre</span>
                      <div className="f-seg full">
                        {OT_CODES.map(c=>(
                          <button key={c.value} className={"seg"+(form.otCode===c.value?" on":"")}
                            onClick={()=>setForm(f=>({...f, otCode:c.value, ...(c.value==="60"?{compWageType:"0",doubleOt:false}:{})}))}>
                            {(c.label.split("— ")[1]||c.label).trim()}
                          </button>
                        ))}
                      </div>
                      {form.otCode==="60"&&(
                        <div style={{fontSize:11,color:"var(--ink-3)",marginTop:6}}>Fire Control OT is always paid — it can't be banked.</div>
                      )}
                    </div>
                    <div className="f-row">
                      <span className="f-lab">Order / project #</span>
                      <input className="f-inp" type="text" placeholder="" value={form.orderProject} onChange={e=>setForm(f=>({...f,orderProject:e.target.value}))} style={{maxWidth:150}} />
                    </div>
                  </div>
                </>
              )}

              <div className="f-sec">Reason</div>
              <div className="f-card">
                <textarea className="f-reason-inp" placeholder="Reason for overtime or absence…" value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} style={{padding:"14px 16px"}} />
              </div>

              {editId&&(
                <div style={{padding:"18px 16px 0"}}>
                  <button onClick={()=>{ setShowForm(false); requestDelete(selDate, editId); }} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:"var(--fire)"}}>Delete entry</button>
                </div>
              )}
              <div style={{height:24}} />
            </div>

            <div className="scr-bottom solid">
              <div className="f-total">Total <b>{fmtHrs(fHrs)} hrs</b></div>
              <button className="cta" style={{flex:1}} disabled={!fTot} onClick={saveEntry}><span style={{fontSize:17,lineHeight:1}}>✓</span> {editId?"Update entry":"Save entry"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit expense — full-screen form sheet */}
      {showExpForm&&(
        <div className="a-sheet">
          <div className="a-form-col">
            <div className="a-form-bar x-bar">
              <button className="a-fbtn" onClick={()=>{setShowExpForm(false);setShowCamera(false);}}>Cancel</button>
              <span className="a-ftitle">{expEditId?"Edit expense":"New expense"}</span>
              <button className="a-fbtn save x-save" disabled={!expSaveValid} style={{opacity:expSaveValid?1:.4}} onClick={saveExpense}>Save</button>
            </div>

            <div className="scr-body a-form">
              {!expEditId&&lastExp&&(
                <button className="quickfill" onClick={()=>setExpForm(f=> PER_DIEM_KEYS.includes(lastExp.type)
                  ? ({...f, meals:{[lastExp.type]:lastExp.amount}, project:lastExp.project, reason:lastExp.reason, location:lastExp.location, costCentre:lastExp.costCentre||"62"})
                  : ({...f, meals:{}, type:lastExp.type, amount:lastExp.amount, project:lastExp.project, reason:lastExp.reason, location:lastExp.location, costCentre:lastExp.costCentre||"62"}))}>
                  <span className="qf-ico" style={{fontSize:15}}>↺</span>
                  <span className="qf-txt">
                    <b>Fill from last claim</b>
                    <span>{EXP_TYPES[lastExp.type]?.label||lastExp.type} · {lastExp.project||"no project"} · {money(parseFloat(lastExp.amount)||0)}</span>
                  </span>
                  <span style={{color:"var(--ink-3)",fontSize:16}}>›</span>
                </button>
              )}

              {/* Per-diem quick chips — multi-select, new expenses only */}
              {!expEditId&&(
                <>
                  <div className="f-sec">Per diem</div>
                  <div className="f-types">
                    {PER_DIEM_KEYS.map(k=>(
                      <button key={k} className={"ftype k-exp"+(expForm.meals[k]!=null?" on":"")} style={{justifyContent:"flex-start"}} onClick={()=>toggleMeal(k)}>
                        <span className="exp-ico"><MIcon n={EXP_TYPES[k].icon} s={14} /></span>{EXP_TYPES[k].label} · {money(PER_DIEM_RATES[k])}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {perDiemMode?(
                <>
                  <div className="f-sec">Per diem amounts</div>
                  <div className="f-card">
                    {allThreeSelected?(
                      <div className="f-row">
                        <span className="f-lab">Full Day Meal Per Diem</span>
                        <span style={{display:"flex",alignItems:"center",gap:4}}>
                          <span style={{color:"var(--ink-3)"}}>$</span>
                          <input className="f-inp" inputMode="decimal" value={expForm.fulldayAmt}
                            onChange={e=>{ const v=e.target.value.replace(/[^0-9.]/g,"").replace(/(\..*)\./g,"$1"); setExpForm(f=>({...f,fulldayAmt:v})); }}
                            style={{width:36,textAlign:"left"}} />
                        </span>
                      </div>
                    ):(
                      PER_DIEM_KEYS.filter(m=>expForm.meals[m]!=null).map((m,i,arr)=>(
                        <div className="f-row" key={m} style={{borderBottom:i<arr.length-1?"1px solid var(--line-soft)":"none"}}>
                          <span className="f-lab">{EXP_TYPES[m].label}</span>
                          <span style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{color:"var(--ink-3)"}}>$</span>
                            <input className="f-inp" inputMode="decimal" value={expForm.meals[m]}
                              onChange={e=>setMealAmount(m,e.target.value)} style={{width:36,textAlign:"left"}} />
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ):(
                <>
                  <div className="f-sec">Type</div>
                  {(()=>{
                    const dropKeys = expEditId ? ["fullday",...OTHER_EXP_KEYS] : OTHER_EXP_KEYS;
                    const mealOn   = PER_DIEM_KEYS.includes(expForm.type);
                    const dropOn   = !mealOn && dropKeys.includes(expForm.type);
                    return (<>
                      {expEditId && (
                        <div className="f-types">
                          {PER_DIEM_KEYS.map(k=>(
                            <button key={k} className={"ftype k-exp"+(expForm.type===k?" on":"")}
                              onClick={()=>setExpForm(f=>({...f,type:k}))}>
                              <MIcon n="meal" s={13}/>
                              {EXP_TYPES[k].label}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className={"f-card f-other x-exp-drop"+(dropOn?" on":"")}>
                        <div className="f-row f-center" style={{position:"relative"}}>
                          <span className={"f-val f-date"+(dropOn?"":" ph")}>
                            {dropOn ? (EXP_TYPES[expForm.type]?.label||expForm.type) : "Other expense types…"}
                          </span>
                          <span aria-hidden="true" className="f-other-arrow">▾</span>
                          <select className="f-other-sel" aria-label="Expense type"
                            value={dropOn ? expForm.type : ""}
                            onChange={e=>e.target.value&&setExpForm(f=>({...f,type:e.target.value}))}>
                            <option value="">Other expense types…</option>
                            {dropKeys.map(k=>(<option key={k} value={k}>{EXP_TYPES[k]?.label||k}</option>))}
                          </select>
                        </div>
                      </div>
                    </>);
                  })()}

                  <div className="f-sec">Amount</div>
                  <div className="x-amount">
                    <span className="x-amount-cur">$</span>
                    <input className="x-amount-val" inputMode="decimal" placeholder="0.00" value={expForm.amount}
                      onChange={e=>{ const v=e.target.value.replace(/[^0-9.]/g,"").replace(/(\..*)\./g,"$1"); setExpForm(f=>({...f,amount:v})); }}
                      style={{width:170,textAlign:"center",background:"transparent",border:"none",outline:"none"}} />
                  </div>
                </>
              )}

              <div className="f-sec">Receipt photo</div>
              {expForm.receipt?(
                <div className="x-receipt-card">
                  <div className="receipt-thumb lg" style={{backgroundImage:`url(${expForm.receipt})`,backgroundSize:"cover",backgroundPosition:"center",cursor:"zoom-in"}} onClick={()=>setReceiptView({url:expForm.receipt,name:receiptFilename(expForm.type,expForm.date)})} />
                  <div className="x-receipt-info">
                    <div className="x-receipt-name">Receipt attached</div>
                    <div className="x-receipt-meta">Tap photo to view</div>
                    <div className="x-receipt-acts">
                      <button className="x-rbtn" style={{border:"none",cursor:"pointer"}} onClick={()=>downloadDataUrl(expForm.receipt,receiptFilename(expForm.type,expForm.date))}>Download</button>
                      <label className="x-rbtn" style={{cursor:"pointer"}}>Replace<input type="file" accept="image/*" hidden onChange={onReceiptPick} /></label>
                      <button className="x-rbtn" style={{color:"var(--fire)",border:"none",cursor:"pointer"}} onClick={()=>setExpForm(f=>({...f,receipt:""}))}>Remove</button>
                    </div>
                  </div>
                </div>
              ):(
                <div className="x-receipt-empty">
                  <div className="x-receipt-empty-ico"><MIcon n="receipt" s={22} /></div>
                  <div className="x-receipt-empty-txt">Add a receipt for this claim</div>
                  <div className="x-receipt-btns">
                    <button type="button" className="x-cap-btn primary" onClick={()=>setShowCamera(true)}><MIcon n="camera" s={16} />Take photo</button>
                    <label className="x-cap-btn" style={{cursor:"pointer"}}><MIcon n="upload" s={16} />Upload photo<input type="file" accept="image/*" hidden onChange={onReceiptPick} /></label>
                  </div>
                </div>
              )}
              {showCamera&&(
                <CameraCapture onCapture={onCameraCapture} onClose={()=>setShowCamera(false)} />
              )}

              <div className="f-sec">Details</div>
              <div className="f-card">
                <div className="f-row">
                  <span className="f-lab">Date</span>
                  <input className="f-inp" type="date" value={expForm.date} onChange={e=>setExpForm(f=>({...f,date:e.target.value}))} style={{maxWidth:160}} />
                </div>
                <div className="f-row">
                  <span className="f-lab">Charge to</span>
                  <div className="f-seg sm">
                    {OT_CODES.map(c=>(
                      <button key={c.value} className={"seg"+(expForm.costCentre===c.value?" on":"")}
                        onClick={()=>setExpForm(f=>({...f,costCentre:c.value}))}>
                        {(c.label.split("— ")[1]||c.label).trim()}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="f-row">
                  <span className="f-lab">Project #</span>
                  <input className="f-inp" type="text" placeholder="" value={expForm.project} onChange={e=>setExpForm(f=>({...f,project:e.target.value}))} style={{maxWidth:150}} />
                </div>
                <div className="f-row">
                  <span className="f-lab">Location</span>
                  <input className="f-inp" type="text" placeholder="" value={expForm.location} onChange={e=>setExpForm(f=>({...f,location:e.target.value}))} style={{maxWidth:150}} />
                </div>
              </div>

              <div className="f-sec">Reason</div>
              <div className="f-card">
                <textarea className="f-reason-inp" placeholder="What was this expense for?" value={expForm.reason} onChange={e=>setExpForm(f=>({...f,reason:e.target.value}))} style={{padding:"14px 16px"}} />
              </div>

              {expEditId&&(
                <div style={{padding:"18px 16px 0"}}>
                  <button onClick={deleteExpenseFromForm} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:"var(--fire)"}}>Delete expense</button>
                </div>
              )}
              <div style={{height:24}} />
            </div>

            <div className="scr-bottom solid">
              <div className="f-total">Total <b className="x-total">{money(expTotal)}</b></div>
              <button className="cta money" style={{flex:1}} disabled={!expSaveValid} onClick={saveExpense}><span style={{fontSize:17,lineHeight:1}}>✓</span> {expEditId?"Update":allThreeSelected?"Save full day":perDiemMode&&Object.keys(expForm.meals).length>1?"Save per diems":"Save expense"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt viewer */}
      {receiptView&&(
        <div className="modal-bg" style={{cursor:"zoom-out",padding:24,flexDirection:"column",gap:14}} onClick={()=>setReceiptView(null)}>
          <img src={receiptView.url} alt="Receipt" style={{maxWidth:"100%",maxHeight:"82vh",borderRadius:12,boxShadow:"var(--shadow-modal)"}} onClick={e=>e.stopPropagation()} />
          <button className="export-btn" style={{cursor:"pointer"}} onClick={e=>{e.stopPropagation();downloadDataUrl(receiptView.url,receiptView.name);}}>Download receipt</button>
        </div>
      )}

      {/* Delete Expense Modal */}
      {confirmDeleteExp&&(
        <div className="modal-bg">
          <div className="modal" style={{maxWidth:360}}>
            <h2 style={{fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:8}}>Delete expense?</h2>
            <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,marginBottom:24}}>This expense and its receipt will be permanently removed. This can't be undone.</p>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn-ghost" onClick={()=>setConfirmDeleteExp(null)}>Cancel</button>
              <button className="btn-primary" style={{background:"#cc3333",color:"#fff"}} onClick={confirmDelExp}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Day note editor */}
      {noteEdit&&(
        <div className="modal-bg" onClick={e=>{if(e.target===e.currentTarget)setNoteEdit(null);}}>
          <div className="modal" style={{maxWidth:420}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <h2 style={{fontSize:18,fontWeight:700,color:"var(--ink)"}}>Day note</h2>
                <p style={{fontSize:12,color:"var(--ink-2)",marginTop:2}}>{dateLbl(noteEdit.date).full}</p>
              </div>
              <button className="btn-icon" style={{fontSize:18,lineHeight:1,padding:"4px 6px"}} onClick={()=>setNoteEdit(null)}>×</button>
            </div>
            <div className="field" style={{marginBottom:16}}>
              <textarea autoFocus value={noteEdit.text} onChange={e=>setNoteEdit(n=>({...n,text:e.target.value}))} style={{minHeight:120}} />
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              {dayNotes[noteEdit.date]&&dayNotes[noteEdit.date].note&&(
                <button className="btn-ghost" style={{color:"var(--fire)",borderColor:"var(--fire)"}}
                  onClick={()=>setConfirmDeleteNote(noteEdit.date)}>
                  Delete
                </button>
              )}
              <div style={{flex:1}} />
              <button className="btn-ghost" onClick={()=>setNoteEdit(null)}>Cancel</button>
              <button className="cta" style={{flex:"none",padding:"12px 20px"}} onClick={saveDayNote}>Save note</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete&&(
        <div className="modal-bg">
          <div className="modal" style={{maxWidth:360}}>
            <h2 style={{fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:8}}>Delete Entry?</h2>
            <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,marginBottom:24}}>This entry will be permanently removed. This can't be undone.</p>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn-ghost" onClick={()=>setConfirmDelete(null)}>Cancel</button>
              <button className="btn-primary" style={{background:"#cc3333",color:"#fff"}} onClick={confirmDelEntry}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteNote&&(
        <div className="modal-bg">
          <div className="modal" style={{maxWidth:360}}>
            <h2 style={{fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:8}}>Delete note?</h2>
            <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,marginBottom:24}}>This day note will be permanently removed. This can't be undone.</p>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn-ghost" onClick={()=>setConfirmDeleteNote(null)}>Cancel</button>
              <button className="btn-primary" style={{background:"#cc3333",color:"#fff"}} onClick={confirmDelNote}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {importData&&(
        <div className="modal-bg">
          <div className="modal" style={{maxWidth:380}}>
            <h2 style={{fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:8}}>Import this backup?</h2>
            <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,marginBottom:20}}>
              This adds <strong style={{color:"var(--text)"}}>{importData.entries} entries</strong>, <strong style={{color:"var(--text)"}}>{importData.expenses} expenses</strong>, and <strong style={{color:"var(--text)"}}>{importData.notes} notes</strong> from the file to your account. Anything already here is kept — nothing is deleted, and duplicates are skipped.
            </p>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn-ghost" onClick={()=>setImportData(null)}>Cancel</button>
              <button className="btn-primary" onClick={()=>doImport(importData.raw)}>Import</button>
            </div>
          </div>
        </div>
      )}

      {updateReady&&(
        <div className="sw-toast" role="status">
          <span>Update available</span>
          <button type="button" className="sw-toast-btn" onClick={()=>{applyUpdate(); setUpdateReady(false);}}>Refresh</button>
          <button type="button" className="sw-toast-x" aria-label="Dismiss" onClick={()=>setUpdateReady(false)}>×</button>
        </div>
      )}

      {/* Diagnostic only — surfaces the raw PB write error on-screen (no devtools
          needed on a phone) while we track down the expense-receipt sync bug. */}
      {syncSnap.lastError&&syncSnap.lastError!==dismissedSyncError&&(
        <div className="sw-toast" role="status" style={{maxWidth:"90vw",whiteSpace:"normal"}}>
          <span>Sync error: {syncSnap.lastError}</span>
          <button type="button" className="sw-toast-x" aria-label="Dismiss" onClick={()=>setDismissedSyncError(syncSnap.lastError)}>×</button>
        </div>
      )}

    </div>
  );
}
